import React, { useState, useEffect } from 'react';
import { get, put, post, del } from 'aws-amplify/api';
import { updateUserAttribute, confirmUserAttribute, setUpTOTP, verifyTOTPSetup, updateMFAPreference, fetchMFAPreference, associateWebAuthnCredential, listWebAuthnCredentials, deleteWebAuthnCredential } from 'aws-amplify/auth';
import QRCode from 'qrcode';
import './Settings.css';
import TaskManagement from './TaskManagement';
import BillingPage from './BillingPage';
import { getLocalDateString } from '../utils/date';

function Settings({ user, onBack, onTasksUpdated }) {
  const isAdmin = user?.role === 'admin';
  const [activeSection, setActiveSection] = useState('company');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  // 管理者によるユーザー編集
  const [editingUser, setEditingUser] = useState(null);
  const [userEditForm, setUserEditForm] = useState({});
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [isEditingUserDetail, setIsEditingUserDetail] = useState(false); // 詳細ページ内インライン編集
  // 新規ユーザー追加ダイアログ
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [addUserForm, setAddUserForm] = useState({ username: '', email: '', display_name: '', last_name: '', first_name: '', role: 'user', department: '', position: '', phone: '' });
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [addUserError, setAddUserError] = useState('');
  const [createdUserInfo, setCreatedUserInfo] = useState(null); // 作成完了ダイアログ用
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  // メール変更ダイアログ
  const [isEmailChangeOpen, setIsEmailChangeOpen] = useState(false);
  const [emailChangeStep, setEmailChangeStep] = useState(1); // 1: 新メール入力, 2: コード確認
  const [newEmail, setNewEmail] = useState('');
  const [emailChangeCode, setEmailChangeCode] = useState('');
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [emailChangeError, setEmailChangeError] = useState('');
  // 表示用（保存後のみ更新）
  const [profileData, setProfileData] = useState({
    lastName: user?.last_name || '',
    firstName: user?.first_name || '',
    username: user?.username || '',
    displayName: user?.display_name || user?.attributes?.email?.split('@')[0] || '',
    department: user?.department || '',
    position: user?.position || '',
    phone: user?.phone || '',
  });
  // ダイアログ入力用（ダイアログを開いた時に profileData からコピー）
  const [editForm, setEditForm] = useState({ ...profileData });

  // IPアドレス制限
  const [ipPresets, setIpPresets] = useState([]);
  const [ipPresetsLoading, setIpPresetsLoading] = useState(false);
  const [ipPresetForm, setIpPresetForm] = useState({ name: '', cidrs: '' });
  const [ipPresetError, setIpPresetError] = useState('');
  const [editingPreset, setEditingPreset] = useState(null);
  const [userIpRestrictions, setUserIpRestrictions] = useState({}); // { userId: [restriction...] }
  const [ipAddForm, setIpAddForm] = useState({ ip_cidr: '', label: '' });
  const [ipAddError, setIpAddError] = useState('');
  const [bulkApplyDialog, setBulkApplyDialog] = useState(null); // preset
  const [bulkSelectedUsers, setBulkSelectedUsers] = useState([]);
  const [currentIp, setCurrentIp] = useState('');

  // MFA設定
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [mfaStatus, setMfaStatus] = useState(null); // null | 'enabled' | 'disabled'
  const [mfaStep, setMfaStep] = useState('check'); // 'check' | 'setup' | 'verify' | 'done' | 'disable_confirm'
  const [mfaQrUrl, setMfaQrUrl] = useState('');
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);

  // パスキー(WebAuthn)管理（管理者用）
  const [passkeyDialogOpen, setPasskeyDialogOpen] = useState(false);
  const [passkeys, setPasskeys] = useState([]);
  const [passkeysLoading, setPasskeysLoading] = useState(false);
  const [passkeyError, setPasskeyError] = useState('');
  const [passkeyAdding, setPasskeyAdding] = useState(false);
  const [passkeyDeleteConfirm, setPasskeyDeleteConfirm] = useState(null); // credentialId

  // ユーザー一覧のソート・ページネーション・詳細ページ
  const [sortField, setSortField] = useState('username');
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null); // 詳細ページ表示対象
  const [selectedUserLoginHistory, setSelectedUserLoginHistory] = useState([]);
  const [selectedUserLoginHistoryLoading, setSelectedUserLoginHistoryLoading] = useState(false);
  const [mfaResetLoading, setMfaResetLoading] = useState(false);
  const [mfaResetConfirm, setMfaResetConfirm] = useState(false);
  const [mfaResetResult, setMfaResetResult] = useState(null); // null | 'success' | 'error'
  const [pwResetConfirm, setPwResetConfirm] = useState(false);
  const [pwResetLoading, setPwResetLoading] = useState(false);
  const [pwResetResult, setPwResetResult] = useState(null); // null | { password: string } | 'error'
  const [pwResetTargetUser, setPwResetTargetUser] = useState(null);
  const PAGE_SIZE = 50;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="sort-icon sort-icon--none">↕</span>;
    return <span className="sort-icon sort-icon--active">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const [isCompanyEditOpen, setIsCompanyEditOpen] = useState(false);
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({ company_name: '', address: '', phone: '', fax: '', session_timeout_minutes: 60 });
  const [tenantInfo, setTenantInfo] = useState(null);
  // ユーザーCSVインポート
  const [isUserCsvImportOpen, setIsUserCsvImportOpen] = useState(false);
  const [userCsvFile, setUserCsvFile] = useState(null);
  const [userCsvPreview, setUserCsvPreview] = useState([]);
  const [userCsvError, setUserCsvError] = useState('');
  const [isImportingUsers, setIsImportingUsers] = useState(false);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0, errors: [] });

  // プロフィール保存
  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      const restOperation = put({
        apiName: 'worktimerApi',
        path: '/users',
        options: {
          body: {
            last_name: editForm.lastName,
            first_name: editForm.firstName,
            username: editForm.username,
            display_name: editForm.displayName,
            department: editForm.department,
            position: editForm.position,
            phone: editForm.phone,
          }
        }
      });
      await restOperation.response;
      // 保存成功時のみ表示データを更新
      setProfileData({ ...editForm });
      setIsEditDialogOpen(false);
    } catch (e) {
      console.error("❌ プロフィール更新エラー:", e);
      alert("保存に失敗しました。");
    } finally {
      setIsSavingProfile(false);
    }
  };

  // ユーザー一覧を取得
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const restOperation = get({
        apiName: 'worktimerApi',
        path: '/users/all'
      });
      const response = await restOperation.response;
      const data = await response.body.json();
      setUsers(data);
    } catch (e) {
      console.error("❌ ユーザー一覧取得エラー:", e);
    } finally {
      setLoadingUsers(false);
    }
  };

  // 管理者によるユーザー編集を開く
  const handleOpenUserEdit = (u) => {
    setEditingUser(u);
    setUserEditForm({
      last_name: u.last_name || '',
      first_name: u.first_name || '',
      username: u.username || '',
      display_name: u.display_name || '',
      department: u.department || '',
      position: u.position || '',
      phone: u.phone || '',
      role: u.role || 'user',
      join_date: u.join_date ? u.join_date.split('T')[0] : '',
    });
  };

  // 新規ユーザー追加
  const handleAddUser = async () => {
    if (!addUserForm.username || !addUserForm.email || !addUserForm.display_name) {
      setAddUserError('ユーザー名・メールアドレス・表示名は必須です');
      return;
    }
    setIsAddingUser(true);
    setAddUserError('');
    try {
      const op = post({
        apiName: 'worktimerApi',
        path: '/users',
        options: { body: addUserForm }
      });
      const res = await op.response;
      const data = await res.body.json();
      setIsAddUserOpen(false);
      setCreatedUserInfo({
        username: addUserForm.username,
        email: addUserForm.email,
        display_name: addUserForm.display_name,
        temporary_password: data.temporary_password,
      });
      setAddUserForm({ username: '', email: '', display_name: '', last_name: '', first_name: '', role: 'user', department: '', position: '', phone: '' });
      await fetchUsers();
    } catch (e) {
      let detail = '不明なエラー';
      try {
        const body = await e.response.body.json();
        if (body.error === 'user_limit_exceeded') {
          setIsAddUserOpen(false);
          if (window.confirm(`ユーザー数の上限（${body.max_users}名）に達しています。\nユーザー数を増やすにはお問い合わせが必要です。プラン・課金ページへ移動しますか？`)) {
            setActiveSection('billing');
          }
          return;
        }
        if (body.error === 'username_already_exists') {
          detail = 'このユーザー名は既に使用されています。別のユーザー名を入力してください。';
        } else if (body.error === 'email_already_exists') {
          detail = 'このメールアドレスは既に登録されています。';
        } else {
          detail = body.error || body.message || detail;
        }
      } catch {}
      console.error("❌ ユーザー追加エラー:", detail, e);
      setAddUserError(detail);
    } finally {
      setIsAddingUser(false);
    }
  };

  // ユーザー凍結・凍結解除
  const handleFreezeUser = async (userId, currentFrozen, displayName) => {
    const action = currentFrozen ? '凍結解除' : '凍結';
    if (!window.confirm(`${displayName} を${action}しますか？${!currentFrozen ? '\n凍結すると初期ユーザーフラグが解除されます。' : ''}`)) return;
    try {
      const op = put({
        apiName: 'worktimerApi',
        path: `/users/${userId}/freeze`,
        options: { body: { is_frozen: !currentFrozen } }
      });
      await op.response;
      setUsers(prev => prev.map(u =>
        u.user_id === userId ? { ...u, is_frozen: !currentFrozen, is_initial_user: !currentFrozen ? false : u.is_initial_user } : u
      ));
      setSelectedUser(prev => prev && prev.user_id === userId ? { ...prev, is_frozen: !currentFrozen, is_initial_user: !currentFrozen ? false : prev.is_initial_user } : prev);
    } catch (e) {
      alert(`${action}に失敗しました。`);
      console.error("❌ 凍結エラー:", e);
    }
  };

  // 管理者によるユーザー保存
  const handleSaveUser = async () => {
    if (!editingUser) return;
    setIsSavingUser(true);
    try {
      const restOperation = put({
        apiName: 'worktimerApi',
        path: `/users/${editingUser.user_id}`,
        options: { body: userEditForm }
      });
      await restOperation.response;
      setUsers(prev => prev.map(u =>
        u.user_id === editingUser.user_id ? { ...u, ...userEditForm } : u
      ));
      setSelectedUser(prev => prev && prev.user_id === editingUser.user_id ? { ...prev, ...userEditForm } : prev);
      setEditingUser(null);
      setIsEditingUserDetail(false);
    } catch (e) {
      console.error("❌ ユーザー更新エラー:", e);
      alert("保存に失敗しました。");
    } finally {
      setIsSavingUser(false);
    }
  };

  // テナント情報を取得
  const fetchTenantInfo = async () => {
    try {
      const restOperation = get({ apiName: 'worktimerApi', path: '/tenants/info' });
      const response = await restOperation.response;
      const data = await response.body.json();
      setTenantInfo(data);
    } catch (e) {
      console.error("❌ テナント情報取得エラー:", e);
    }
  };

  // メールアドレス変更: 新しいメールに確認コード送信
  const handleSendEmailChangeCode = async () => {
    if (!newEmail || !newEmail.includes('@')) { setEmailChangeError('有効なメールアドレスを入力してください'); return; }
    setIsChangingEmail(true);
    setEmailChangeError('');
    try {
      await updateUserAttribute({ userAttribute: { attributeKey: 'email', value: newEmail } });
      setEmailChangeStep(2);
    } catch (e) {
      setEmailChangeError(e.message || 'コード送信に失敗しました');
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handleConfirmEmailChange = async () => {
    if (!emailChangeCode) { setEmailChangeError('確認コードを入力してください'); return; }
    setIsChangingEmail(true);
    setEmailChangeError('');
    try {
      await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: emailChangeCode });
      setIsEmailChangeOpen(false);
      setEmailChangeStep(1);
      setNewEmail('');
      setEmailChangeCode('');
      alert('メールアドレスを変更しました。次回ログインから新しいメールアドレスが有効になります。');
    } catch (e) {
      setEmailChangeError(e.message || '確認コードが正しくありません');
    } finally {
      setIsChangingEmail(false);
    }
  };

  // 会社情報保存
  const handleSaveCompany = async () => {
    setIsSavingCompany(true);
    try {
      const op = put({ apiName: 'worktimerApi', path: '/tenants/info', options: { body: companyForm } });
      await op.response;
      await fetchTenantInfo();
      setIsCompanyEditOpen(false);
    } catch (e) {
      console.error("❌ 会社情報保存エラー:", e);
      alert("保存に失敗しました。");
    } finally {
      setIsSavingCompany(false);
    }
  };

  // ユーザーCSVパース
  const parseUserCsv = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return { error: 'データがありません' };
    const header = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const required = ['email', 'display_name'];
    for (const r of required) {
      if (!header.includes(r)) return { error: `列 "${r}" が必要です` };
    }
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const vals = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
      if (vals.every(v => !v)) continue;
      const row = {};
      header.forEach((h, idx) => { row[h] = vals[idx] || ''; });
      if (!row.email) continue;
      rows.push(row);
    }
    if (rows.length === 0) return { error: 'インポートするユーザーがありません' };
    return { rows };
  };

  const handleUserCsvFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUserCsvFile(file);
    setUserCsvError('');
    setUserCsvPreview([]);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = parseUserCsv(ev.target.result);
      if (result.error) { setUserCsvError(result.error); return; }
      setUserCsvPreview(result.rows.slice(0, 5));
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleImportUsers = async () => {
    if (!userCsvFile) return;
    const text = await userCsvFile.text();
    const result = parseUserCsv(text);
    if (result.error) { setUserCsvError(result.error); return; }
    const rows = result.rows;
    setIsImportingUsers(true);
    setImportProgress({ done: 0, total: rows.length, errors: [] });
    const errors = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      try {
        const op = post({
          apiName: 'worktimerApi',
          path: '/users',
          options: { body: {
            email: row.email,
            display_name: row.display_name,
            username: row.username || row.email.split('@')[0],
            role: row.role || 'user',
            department: row.department || '',
            position: row.position || '',
            phone: row.phone || '',
          }}
        });
        await op.response;
      } catch (e) {
        let msg = row.email + ': ';
        try {
          const body = await e.response.body.json();
          if (body.error === 'user_limit_exceeded') {
            msg += 'ユーザー上限に達しました';
            errors.push(msg);
            break;
          }
          msg += body.error || e.message || '不明なエラー';
        } catch { msg += e.message || '不明なエラー'; }
        errors.push(msg);
      }
      setImportProgress({ done: i + 1, total: rows.length, errors: [...errors] });
    }
    setIsImportingUsers(false);
    await fetchUsers();
    if (errors.length === 0) {
      setIsUserCsvImportOpen(false);
      setUserCsvFile(null);
      setUserCsvPreview([]);
    }
  };

  // ログイン履歴（自分用：プロフィールタブ）
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);
  // ログイン履歴（組織全体：管理者用タブ）
  const [orgLoginHistory, setOrgLoginHistory] = useState([]);
  const [orgLoginHistoryLoading, setOrgLoginHistoryLoading] = useState(false);

  // 監査ログ
  const [auditLog, setAuditLog] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);

  const fetchLoginHistory = async () => {
    setLoginHistoryLoading(true);
    try {
      const op = get({ apiName: 'worktimerApi', path: '/users/login-history' });
      const res = await op.response;
      const data = await res.body.json();
      setLoginHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('ログイン履歴取得エラー:', e);
    } finally {
      setLoginHistoryLoading(false);
    }
  };

  const fetchOrgLoginHistory = async () => {
    setOrgLoginHistoryLoading(true);
    try {
      const op = get({ apiName: 'worktimerApi', path: '/tenants/login-history' });
      const res = await op.response;
      const data = await res.body.json();
      setOrgLoginHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('組織ログイン履歴取得エラー:', e);
    } finally {
      setOrgLoginHistoryLoading(false);
    }
  };

  // ユーザー詳細ページへ遷移（ログイン履歴も同時取得）
  const openUserDetail = async (targetUser) => {
    setSelectedUser(targetUser);
    setSelectedUserLoginHistory([]);
    setSelectedUserLoginHistoryLoading(true);
    try {
      const isSelf = targetUser.user_id === user?.user_id;
      const path = (isAdmin && !isSelf)
        ? `/users/${targetUser.user_id}/login-history`
        : '/users/login-history';
      const op = get({ apiName: 'worktimerApi', path });
      const res = await op.response;
      const data = await res.body.json();
      setSelectedUserLoginHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      setSelectedUserLoginHistory([]);
    } finally {
      setSelectedUserLoginHistoryLoading(false);
    }
    // IP制限も取得
    if (isAdmin) {
      fetchUserIpRestrictions(targetUser.user_id);
    }
  };

  const handleMfaReset = async () => {
    if (!selectedUser) return;
    setMfaResetLoading(true);
    setMfaResetResult(null);
    try {
      const op = post({
        apiName: 'worktimerApi',
        path: `/users/${selectedUser.user_id}/reset-mfa`,
        options: { body: {} },
      });
      const res = await op.response;
      const data = await res.body.json();
      setMfaResetResult(data?.note === 'admin_webauthn_not_revocable' ? 'success_admin' : 'success');
    } catch (e) {
      setMfaResetResult('error');
    } finally {
      setMfaResetLoading(false);
      // 完了するまで確認ダイアログ（処理中表示）を出したままにする
      setMfaResetConfirm(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!pwResetTargetUser) return;
    setPwResetLoading(true);
    setPwResetResult(null);
    try {
      const op = post({
        apiName: 'worktimerApi',
        path: `/users/${pwResetTargetUser.user_id}/reset-password`,
        options: { body: {} },
      });
      const res = await op.response;
      const data = await res.body.json();
      setPwResetResult({ password: data.temporary_password, emailSent: !!data.email_sent });
    } catch (e) {
      setPwResetResult('error');
    } finally {
      setPwResetLoading(false);
      // 完了するまで確認ダイアログ（処理中表示）を出したままにする
      setPwResetConfirm(false);
    }
  };

  const fetchAuditLog = async () => {
    setAuditLogLoading(true);
    try {
      const op = get({ apiName: 'worktimerApi', path: '/tenants/audit-log' });
      const res = await op.response;
      const data = await res.body.json();
      setAuditLog(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('監査ログ取得エラー:', e);
    } finally {
      setAuditLogLoading(false);
    }
  };

  // タスク割り当て機能（管理者用）
  const [assignmentDate, setAssignmentDate] = useState(getLocalDateString());
  const [assignments, setAssignments] = useState([]);
  const [assignmentUsers, setAssignmentUsers] = useState([]);
  const [assignmentTasks, setAssignmentTasks] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({ user_id: '', task_id: '', assigned_count: 1 });
  const [savingAssignment, setSavingAssignment] = useState(false);

  const fetchAssignments = async (date) => {
    setLoadingAssignments(true);
    try {
      const op = get({ apiName: 'worktimerApi', path: '/task-assignments', options: { queryParams: { date } } });
      const response = await op.response;
      const data = await response.body.json();
      setAssignments(Array.isArray(data) ? data : []);
    } catch (e) {
      let detail = e.message || '不明なエラー';
      try {
        const body = await e.response.body.json();
        detail = body.error || detail;
      } catch {}
      console.error("❌ 割り当て取得エラー詳細:", detail, e);
      alert("割り当て取得に失敗しました: " + detail);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const fetchAssignmentUsers = async () => {
    try {
      const op = get({ apiName: 'worktimerApi', path: '/users/all' });
      const response = await op.response;
      const data = await response.body.json();
      setAssignmentUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("❌ ユーザー取得エラー:", e);
    }
  };

  const fetchAssignmentTasks = async () => {
    try {
      const op = get({ apiName: 'worktimerApi', path: '/tasks' });
      const response = await op.response;
      const data = await response.body.json();
      setAssignmentTasks((Array.isArray(data) ? data : []).filter(t => t.item_type !== 'folder'));
    } catch (e) {
      console.error("❌ タスク取得エラー:", e);
    }
  };

  const handleSaveAssignment = async () => {
    if (!assignmentForm.user_id || !assignmentForm.task_id) {
      alert("ユーザーとタスクを選択してください");
      return;
    }
    setSavingAssignment(true);
    try {
      const op = post({
        apiName: 'worktimerApi',
        path: '/task-assignments',
        options: {
          body: {
            user_id: assignmentForm.user_id,
            task_id: assignmentForm.task_id,
            assigned_count: Number(assignmentForm.assigned_count),
            assigned_date: assignmentDate,
          }
        }
      });
      await op.response;
      setAssignmentForm({ user_id: '', task_id: '', assigned_count: 1 });
      await fetchAssignments(assignmentDate);
    } catch (e) {
      let detail = e.message || '不明なエラー';
      try {
        const body = await e.response.body.json();
        detail = body.error || detail;
      } catch {}
      console.error("❌ 割り当て保存エラー詳細:", detail, e);
      alert("保存に失敗しました: " + detail);
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm("この割り当てを削除しますか？")) return;
    try {
      const op = del({ apiName: 'worktimerApi', path: `/task-assignments/${assignmentId}` });
      await op.response;
      setAssignments(prev => prev.filter(a => a.assignment_id !== assignmentId));
    } catch (e) {
      console.error("❌ 割り当て削除エラー:", e);
      alert("削除に失敗しました。");
    }
  };

  // 初期ロード時にテナント情報を取得
  useEffect(() => {
    fetchTenantInfo();
  }, []);

  // ダイアログ開放時に背景スクロール禁止
  useEffect(() => {
    const anyOpen = isEditDialogOpen || (!!editingUser && !isEditingUserDetail) || isAddUserOpen || isEmailChangeOpen || isCompanyEditOpen || isUserCsvImportOpen || mfaDialogOpen || !!bulkApplyDialog || mfaResetConfirm;
    document.body.style.overflow = anyOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isEditDialogOpen, editingUser, isEditingUserDetail, isAddUserOpen, isEmailChangeOpen, isCompanyEditOpen, isUserCsvImportOpen, mfaDialogOpen, bulkApplyDialog, mfaResetConfirm]);

  // タブ切り替えで必要なデータを取得
  useEffect(() => {
    if (activeSection === 'users') {
      fetchUsers();
    }
    if (activeSection === 'assignments' && isAdmin) {
      fetchAssignments(assignmentDate);
      fetchAssignmentUsers();
      fetchAssignmentTasks();
    }
    if (activeSection === 'loginHistory') {
      fetchOrgLoginHistory();
    }
    if (activeSection === 'profile') {
      fetchLoginHistory();
    }
    if (activeSection === 'auditLog' && isAdmin) {
      fetchAuditLog();
    }
    if (activeSection === 'ipRestrictions' && isAdmin) {
      fetchIpPresets();
      // 現在のIPアドレスを取得（ログイン履歴から）
      if (!currentIp) {
        get({ apiName: 'worktimerApi', path: '/users/login-history' }).response
          .then(r => r.body.json())
          .then(data => { if (data[0]?.ip_address) setCurrentIp(data[0].ip_address); })
          .catch(() => {});
      }
    }
  }, [activeSection]);

  // MFA操作
  const openMfaDialog = async () => {
    setMfaDialogOpen(true);
    setMfaStep('check');
    setMfaError('');
    setMfaCode('');
    setMfaLoading(true);
    try {
      const pref = await fetchMFAPreference();
      const enabled = pref.preferred === 'TOTP' || (pref.enabled && pref.enabled.includes('TOTP'));
      setMfaStatus(enabled ? 'enabled' : 'disabled');
      setMfaStep(enabled ? 'already_enabled' : 'setup_intro');
    } catch (e) {
      setMfaStatus('disabled');
      setMfaStep('setup_intro');
    } finally {
      setMfaLoading(false);
    }
  };

  const startMfaSetup = async () => {
    setMfaLoading(true);
    setMfaError('');
    try {
      const totpSetupDetails = await setUpTOTP();
      const email = user?.email || user?.attributes?.email || user?.signInDetails?.loginId || 'user';
      const uri = totpSetupDetails.getSetupUri('WorkTimer', email).toString();
      setMfaSecret(totpSetupDetails.sharedSecret);
      const qrDataUrl = await QRCode.toDataURL(uri, { width: 200, margin: 2 });
      setMfaQrUrl(qrDataUrl);
      setMfaStep('scan');
    } catch (e) {
      setMfaError('設定の開始に失敗しました: ' + e.message);
    } finally {
      setMfaLoading(false);
    }
  };

  const verifyMfa = async () => {
    if (!mfaCode.trim() || mfaCode.length !== 6) {
      setMfaError('6桁のコードを入力してください');
      return;
    }
    setMfaLoading(true);
    setMfaError('');
    try {
      await verifyTOTPSetup({ code: mfaCode.trim() });
      await updateMFAPreference({ totp: 'PREFERRED' });
      setMfaStatus('enabled');
      setMfaStep('done');
    } catch (e) {
      setMfaError('コードが正しくありません。認証アプリのコードを確認してください。');
    } finally {
      setMfaLoading(false);
    }
  };

  const disableMfa = async () => {
    setMfaLoading(true);
    setMfaError('');
    try {
      await updateMFAPreference({ totp: 'DISABLED' });
      setMfaStatus('disabled');
      setMfaStep('setup_intro');
    } catch (e) {
      setMfaError('MFAの無効化に失敗しました: ' + e.message);
    } finally {
      setMfaLoading(false);
    }
  };

  // パスキー(WebAuthn)管理（管理者用）
  const openPasskeyDialog = async () => {
    setPasskeyDialogOpen(true);
    await fetchPasskeys();
  };

  const fetchPasskeys = async () => {
    setPasskeysLoading(true);
    setPasskeyError('');
    try {
      const { credentials } = await listWebAuthnCredentials();
      setPasskeys(credentials || []);
    } catch (e) {
      console.error('❌ listWebAuthnCredentials error:', e);
      setPasskeyError('パスキー一覧の取得に失敗しました');
    } finally {
      setPasskeysLoading(false);
    }
  };

  const addPasskey = async () => {
    setPasskeyAdding(true);
    setPasskeyError('');
    try {
      await associateWebAuthnCredential();
      await fetchPasskeys();
      if (!user?.mfa_enabled) {
        await post({ apiName: 'worktimerApi', path: '/users/me/mfa-complete', options: { body: { mfa_type: 'webauthn' } } }).response;
      }
    } catch (e) {
      console.error('❌ associateWebAuthnCredential error:', e.name, e.message);
      setPasskeyError(
        e.name === 'NotAllowedError'
          ? 'パスキーの作成がキャンセルされました'
          : 'パスキーの追加に失敗しました'
      );
    } finally {
      setPasskeyAdding(false);
    }
  };

  const removePasskey = async (credentialId) => {
    setPasskeyError('');
    try {
      await deleteWebAuthnCredential({ credentialId });
      await fetchPasskeys();
    } catch (e) {
      console.error('❌ deleteWebAuthnCredential error:', e);
      setPasskeyError('削除に失敗しました');
    } finally {
      setPasskeyDeleteConfirm(null);
    }
  };

  // IP制限操作
  const fetchIpPresets = async () => {
    setIpPresetsLoading(true);
    try {
      const op = get({ apiName: 'worktimerApi', path: '/ip-restrictions/presets' });
      const res = await op.response;
      const data = await res.body.json();
      setIpPresets(Array.isArray(data) ? data : []);
    } catch (e) { setIpPresets([]); } finally { setIpPresetsLoading(false); }
  };

  const fetchUserIpRestrictions = async (userId) => {
    try {
      const op = get({ apiName: 'worktimerApi', path: `/users/${userId}/ip-restrictions` });
      const res = await op.response;
      const data = await res.body.json();
      setUserIpRestrictions(prev => ({ ...prev, [userId]: Array.isArray(data) ? data : [] }));
    } catch (e) { setUserIpRestrictions(prev => ({ ...prev, [userId]: [] })); }
  };

  const addUserIpRestriction = async (userId, ip_cidr, label) => {
    setIpAddError('');
    if (!ip_cidr.trim()) { setIpAddError('IPアドレスまたはCIDRを入力してください'); return false; }
    try {
      const op = post({ apiName: 'worktimerApi', path: `/users/${userId}/ip-restrictions`, options: { body: { ip_cidr: ip_cidr.trim(), label: label.trim() || null } } });
      await op.response;
      await fetchUserIpRestrictions(userId);
      setIpAddForm({ ip_cidr: '', label: '' });
      return true;
    } catch (e) { setIpAddError('追加に失敗しました'); return false; }
  };

  const deleteUserIpRestriction = async (userId, id) => {
    try {
      const op = del({ apiName: 'worktimerApi', path: `/users/${userId}/ip-restrictions/${id}` });
      await op.response;
      await fetchUserIpRestrictions(userId);
    } catch (e) { alert('削除に失敗しました'); }
  };

  const saveIpPreset = async () => {
    setIpPresetError('');
    if (!ipPresetForm.name.trim()) { setIpPresetError('プリセット名を入力してください'); return; }
    const cidrs = ipPresetForm.cidrs.split('\n').map(s => s.trim()).filter(Boolean);
    if (cidrs.length === 0) { setIpPresetError('IPアドレスを1件以上入力してください'); return; }
    try {
      if (editingPreset) {
        const op = put({ apiName: 'worktimerApi', path: `/ip-restrictions/presets/${editingPreset.id}`, options: { body: { name: ipPresetForm.name, cidrs } } });
        await op.response;
      } else {
        const op = post({ apiName: 'worktimerApi', path: '/ip-restrictions/presets', options: { body: { name: ipPresetForm.name, cidrs } } });
        await op.response;
      }
      setEditingPreset(null);
      setIpPresetForm({ name: '', cidrs: '' });
      await fetchIpPresets();
    } catch (e) { setIpPresetError('保存に失敗しました'); }
  };

  const deleteIpPreset = async (id) => {
    if (!window.confirm('このプリセットを削除しますか？')) return;
    try {
      const op = del({ apiName: 'worktimerApi', path: `/ip-restrictions/presets/${id}` });
      await op.response;
      await fetchIpPresets();
    } catch (e) { alert('削除に失敗しました'); }
  };

  const applyPresetBulk = async () => {
    if (!bulkApplyDialog || bulkSelectedUsers.length === 0) return;
    try {
      const op = post({ apiName: 'worktimerApi', path: `/ip-restrictions/presets/${bulkApplyDialog.id}/apply`, options: { body: { user_ids: bulkSelectedUsers } } });
      await op.response;
      setBulkApplyDialog(null);
      setBulkSelectedUsers([]);
      // 適用済みユーザーの制限を再取得
      for (const uid of bulkSelectedUsers) { await fetchUserIpRestrictions(uid); }
    } catch (e) { alert('一括適用に失敗しました'); }
  };

  // 現在のIPが制限範囲外かチェック
  const checkCurrentIpWarning = (restrictions) => {
    if (!currentIp || restrictions.length === 0) return false;
    return !restrictions.some(r => ipInCidr(currentIp, r.ip_cidr));
  };

  // CIDR判定をフロントでも行う（警告用）
  function ipInCidr(ip, cidr) {
    try {
      if (!cidr.includes('/')) return ip === cidr;
      const [range, bits] = cidr.split('/');
      const maskBits = parseInt(bits, 10);
      if (isNaN(maskBits)) return false;
      const toInt = (a) => a.split('.').reduce((acc, b) => ((acc << 8) | parseInt(b)), 0) >>> 0;
      const mask = maskBits === 0 ? 0 : ((0xFFFFFFFF << (32 - maskBits)) >>> 0);
      return (toInt(ip) & mask) === (toInt(range) & mask);
    } catch { return false; }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ja-JP');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    return `${d.getFullYear()}/${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`;
  };

  // ログイン履歴をCSVでダウンロード（過去6ヶ月分、画面には直近20件のみ表示）
  const downloadLoginHistoryCsv = (rows, filenamePrefix) => {
    const header = ['日時', 'ユーザー名', 'IPアドレス', 'ブラウザ', 'User-Agent'];
    const escapeCsv = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;
    const lines = [header.map(escapeCsv).join(',')];
    rows.forEach(h => {
      lines.push([
        formatDateTime(h.logged_in_at),
        h.username || h.display_name || '',
        h.ip_address || '',
        h.browser || '',
        h.user_agent || '',
      ].map(escapeCsv).join(','));
    });
    const csvContent = '﻿' + lines.join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filenamePrefix}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getActionLabel = (action) => {
    const labels = {
      company_info_updated: '会社情報を変更',
      user_created: 'ユーザーを追加',
      user_updated: 'ユーザー情報を変更',
      task_created: 'タスクを作成',
      task_updated: 'タスクを更新',
      task_archived: 'タスクをアーカイブ',
      task_restored: 'タスクを復元',
      task_moved: 'タスクを移動',
      max_users_updated: 'ユーザー上限を変更',
      plan_changed: 'プランを変更',
      password_reset: 'パスワードをリセット',
      mfa_reset: 'MFAをリセット',
      mfa_registered: '二段階認証を登録',
      user_frozen: 'ユーザーを凍結',
      user_unfrozen: 'ユーザーの凍結を解除',
    };
    return labels[action] || action;
  };

  const getFieldLabel = (field) => {
    const labels = {
      company_name: '会社名', address: '所在地', phone: '電話番号', fax: 'FAX',
      last_name: '姓', first_name: '名', username: 'ユーザー名', display_name: '表示名',
      department: '部署', position: '役職', role: '役割', join_date: '入社日', max_users: 'ユーザー上限',
    };
    return labels[field] || field;
  };

  const getRoleBadge = (role) => {
    const badges = {
      'admin': { text: '管理者', color: '#EF4444' },
      'manager': { text: 'マネージャー', color: '#F59E0B' },
      'user': { text: '一般ユーザー', color: '#3B82F6' },
      'member': { text: 'メンバー', color: '#3B82F6' }
    };
    const badge = badges[role] || { text: role || '-', color: '#6B7280' };
    return (
      <span
        className="role-badge"
        style={{ backgroundColor: badge.color }}
      >
        {badge.text}
      </span>
    );
  };

  return (
    <>
    <div className="settings">
      <div className="settings-header">
        <button onClick={onBack} className="back-button">
          ← 戻る
        </button>
        <h1>⚙️ 設定</h1>
      </div>

      <div className="settings-container">
        <div className="settings-sidebar">
          <button
            className={activeSection === 'company' ? 'active' : ''}
            onClick={() => setActiveSection('company')}
          >
            🏢 会社概要
          </button>
          <button
            className={activeSection === 'tasks' ? 'active' : ''}
            onClick={() => setActiveSection('tasks')}
          >
            📋 タスク管理
          </button>
          <button
            className={activeSection === 'profile' ? 'active' : ''}
            onClick={() => setActiveSection('profile')}
          >
            👤 マイプロフィール
          </button>
          <button
            className={activeSection === 'users' ? 'active' : ''}
            onClick={() => setActiveSection('users')}
          >
            👥 ユーザー一覧
          </button>
          {isAdmin && (
            <button
              className={activeSection === 'assignments' ? 'active' : ''}
              onClick={() => setActiveSection('assignments')}
            >
              📌 タスク割り当て
            </button>
          )}
          <button
            className={activeSection === 'loginHistory' ? 'active' : ''}
            onClick={() => setActiveSection('loginHistory')}
          >
            🔒 ログイン履歴
          </button>
          {isAdmin && (
            <button
              className={activeSection === 'auditLog' ? 'active' : ''}
              onClick={() => setActiveSection('auditLog')}
            >
              📋 変更履歴
            </button>
          )}
          {isAdmin && (
            <button
              className={activeSection === 'ipRestrictions' ? 'active' : ''}
              onClick={() => setActiveSection('ipRestrictions')}
            >
              🔐 IPアドレス制限
            </button>
          )}
          {isAdmin && (
            <button
              className={activeSection === 'billing' ? 'active' : ''}
              onClick={() => setActiveSection('billing')}
            >
              💳 プラン・課金
            </button>
          )}
          <button
            className={activeSection === 'faq' ? 'active' : ''}
            onClick={() => setActiveSection('faq')}
          >
            ❓ よくある質問
          </button>
        </div>

        <div className="settings-content">
          {activeSection === 'company' && (
            <>
              <div className="settings-section">
                <div className="section-header">
                  <h2>🏢 会社概要</h2>
                  {isAdmin && (
                    <button className="edit-button" onClick={() => {
                      setCompanyForm({
                        company_name: tenantInfo?.company_name || '',
                        address: tenantInfo?.address || '',
                        phone: tenantInfo?.phone || '',
                        fax: tenantInfo?.fax || '',
                        session_timeout_minutes: tenantInfo?.session_timeout_minutes || 60,
                      });
                      setIsCompanyEditOpen(true);
                    }}>✏️ 編集</button>
                  )}
                </div>

                <div className="section-content">
                  {!tenantInfo ? (
                    <div className="loading-container"><div className="loading-spinner"></div><p>読み込み中...</p></div>
                  ) : (
                    <>
                      <h3>基本情報</h3>
                      <div className="info-grid">
                        <div className="info-item">
                          <div className="info-label">テナントID</div>
                          <div className="info-value" style={{ fontFamily: 'monospace', color: '#6B7280' }}>
                            {tenantInfo.tenant_id || '-'}
                          </div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">会社名</div>
                          <div className="info-value">{tenantInfo.company_name || '-'}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">所在地</div>
                          <div className="info-value">{tenantInfo.address || '-'}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">電話番号</div>
                          <div className="info-value">{tenantInfo.phone || '-'}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">FAX</div>
                          <div className="info-value">{tenantInfo.fax || '-'}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">メールアドレス</div>
                          <div className="info-value">{tenantInfo.email || '-'}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">登録日</div>
                          <div className="info-value">{tenantInfo.created_at ? new Date(tenantInfo.created_at).toLocaleDateString('ja-JP') : '-'}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">セッションタイムアウト</div>
                          <div className="info-value">{
                            (() => {
                              const m = tenantInfo.session_timeout_minutes || 60;
                              if (m < 60) return `${m}分`;
                              if (m % 60 === 0) return `${m / 60}時間`;
                              return `${Math.floor(m / 60)}時間${m % 60}分`;
                            })()
                          }</div>
                        </div>
                      </div>

                      <h3>プラン情報</h3>
                      <div className="info-grid">
                        <div className="info-item">
                          <div className="info-label">プラン</div>
                          <div className="info-value">{tenantInfo.subscription_status === 'active' ? '有料プラン' : '無料プラン'}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">ユーザー数</div>
                          <div className="info-value">{tenantInfo.current_users} / {tenantInfo.max_users} 名</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">タスク数</div>
                          <div className="info-value">{tenantInfo.current_tasks} 件</div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {activeSection === 'tasks' && (
            <TaskManagement onTasksUpdated={onTasksUpdated} isAdmin={isAdmin} />
          )}

          {activeSection === 'profile' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>👤 マイプロフィール</h2>
                <button className="edit-button" onClick={() => { setEditForm({ ...profileData }); setIsEditDialogOpen(true); }}>✏️ 編集</button>
              </div>

              <div className="section-content">
                <h3>基本情報</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <div className="info-label">ユーザーID</div>
                    <div className="info-value" style={{ fontFamily: 'monospace', color: '#6B7280' }}>
                      {user?.user_id || '-'}
                    </div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">姓</div>
                    <div className="info-value">{profileData.lastName || '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">名</div>
                    <div className="info-value">{profileData.firstName || '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">表示名</div>
                    <div className="info-value">{profileData.displayName || '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">ユーザー名</div>
                    <div className="info-value">{profileData.username || '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">役割</div>
                    <div className="info-value">{getRoleBadge(user?.role)}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">メールアドレス</div>
                    <div className="info-value">{user?.email || user?.attributes?.email || user?.signInDetails?.loginId || '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">部署</div>
                    <div className="info-value">{profileData.department || '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">役職</div>
                    <div className="info-value">{profileData.position || '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">電話番号</div>
                    <div className="info-value">{profileData.phone || '-'}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">入社日</div>
                    <div className="info-value">{formatDate(user?.join_date)}</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">登録日</div>
                    <div className="info-value">{formatDate(user?.created_at)}</div>
                  </div>
                </div>

                <div className="mfa-section">
                  {isAdmin ? (
                    <div className="mfa-section-header">
                      <div>
                        <h3 style={{ margin: 0 }}>🔑 パスキー管理</h3>
                        <p className="mfa-desc">
                          管理者はパスワードの代わりに生体認証・セキュリティキーでログインします。
                          端末紛失対策として、複数のパスキーを登録しておくことを推奨します。
                        </p>
                      </div>
                      <button className="mfa-setup-btn" onClick={openPasskeyDialog}>
                        パスキー管理
                      </button>
                    </div>
                  ) : (
                    <div className="mfa-section-header">
                      <div>
                        <h3 style={{ margin: 0 }}>二段階認証（MFA）</h3>
                        <p className="mfa-desc">Google Authenticator などの認証アプリを使った二段階認証です。</p>
                      </div>
                      <button className="mfa-setup-btn" onClick={openMfaDialog}>
                        MFA設定
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '24px' }}>
                  <h3 style={{ margin: 0 }}>🔒 ログイン履歴</h3>
                  <button
                    className="edit-button"
                    onClick={() => downloadLoginHistoryCsv(loginHistory, 'my_login_history')}
                    disabled={loginHistory.length === 0}
                  >
                    ⬇ CSVダウンロード
                  </button>
                </div>
                {loginHistoryLoading ? (
                  <div className="loading-container"><div className="loading-spinner"></div><p>読み込み中...</p></div>
                ) : loginHistory.length === 0 ? (
                  <div className="empty-state"><p>ログイン履歴はありません</p></div>
                ) : (
                  <>
                    <p className="history-note">
                      直近20件を表示しています（過去6ヶ月・全{loginHistory.length}件）。全件はCSVダウンロードでご確認ください。
                    </p>
                    <div className="history-table-container">
                      <table className="history-table">
                        <thead>
                          <tr>
                            <th>日時</th>
                            <th>IPアドレス</th>
                            <th>ブラウザ</th>
                            <th>User-Agent</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loginHistory.slice(0, 20).map(h => (
                            <tr key={h.id}>
                              <td className="history-datetime">{formatDateTime(h.logged_in_at)}</td>
                              <td className="history-ip">{h.ip_address || '-'}</td>
                              <td><span className="history-browser-badge">{h.browser || '-'}</span></td>
                              <td className="history-ua" title={h.user_agent}>{h.user_agent ? h.user_agent.substring(0, 60) + (h.user_agent.length > 60 ? '…' : '') : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {activeSection === 'users' && (() => {
            // ユーザー詳細ページ
            if (selectedUser) {
              const canViewHistory = isAdmin || selectedUser.user_id === user?.user_id;
              return (
                <div className="settings-section user-detail-page">
                  <div className="user-detail-header">
                    <button className="user-detail-back" onClick={() => { setSelectedUser(null); setSelectedUserLoginHistory([]); setIsEditingUserDetail(false); setEditingUser(null); }}>
                      ← ユーザー一覧
                    </button>
                    <h2>{selectedUser.display_name || selectedUser.username || 'ユーザー詳細'}</h2>
                    {isAdmin && !isEditingUserDetail && (
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                        <button
                          className="mfa-reset-btn"
                          onClick={() => { setMfaResetConfirm(true); setMfaResetResult(null); }}
                          disabled={mfaResetLoading}
                        >
                          🔐 MFAリセット
                        </button>
                        <button
                          className="edit-button"
                          style={{
                            backgroundColor: selectedUser.is_frozen ? '#D1FAE5' : '#FEE2E2',
                            color: selectedUser.is_frozen ? '#065F46' : '#991B1B',
                            border: `1px solid ${selectedUser.is_frozen ? '#6EE7B7' : '#FCA5A5'}`
                          }}
                          onClick={() => handleFreezeUser(selectedUser.user_id, selectedUser.is_frozen, selectedUser.display_name || selectedUser.username)}
                        >
                          {selectedUser.is_frozen ? '🔓 解除' : '🔒 凍結'}
                        </button>
                        <button
                          className="edit-button"
                          style={{ backgroundColor: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D' }}
                          onClick={() => { setPwResetTargetUser(selectedUser); setPwResetResult(null); setPwResetConfirm(true); }}
                        >
                          🔑 PW
                        </button>
                        <button className="edit-button" onClick={() => { handleOpenUserEdit(selectedUser); setIsEditingUserDetail(true); }}>
                          ✏️ 編集
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="section-content">
                    <h3>基本情報</h3>
                    {isEditingUserDetail ? (
                      <>
                        <div className="info-grid">
                          <div className="form-group">
                            <label>姓</label>
                            <input type="text" value={userEditForm.last_name} onChange={(e) => setUserEditForm({ ...userEditForm, last_name: e.target.value })} placeholder="姓を入力" disabled={isSavingUser} />
                          </div>
                          <div className="form-group">
                            <label>名</label>
                            <input type="text" value={userEditForm.first_name} onChange={(e) => setUserEditForm({ ...userEditForm, first_name: e.target.value })} placeholder="名を入力" disabled={isSavingUser} />
                          </div>
                          <div className="form-group">
                            <label>ユーザー名</label>
                            <input type="text" value={userEditForm.username} onChange={(e) => setUserEditForm({ ...userEditForm, username: e.target.value })} placeholder="ユーザー名を入力" disabled={isSavingUser} />
                          </div>
                          <div className="form-group">
                            <label>表示名</label>
                            <input type="text" value={userEditForm.display_name} onChange={(e) => setUserEditForm({ ...userEditForm, display_name: e.target.value })} placeholder="表示名を入力" disabled={isSavingUser} />
                          </div>
                          <div className="form-group">
                            <label>役割</label>
                            <select value={userEditForm.role} onChange={(e) => setUserEditForm({ ...userEditForm, role: e.target.value })} disabled={isSavingUser}>
                              <option value="user">一般ユーザー</option>
                              <option value="admin">管理者</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label>部署</label>
                            <input type="text" value={userEditForm.department} onChange={(e) => setUserEditForm({ ...userEditForm, department: e.target.value })} placeholder="部署を入力" disabled={isSavingUser} />
                          </div>
                          <div className="form-group">
                            <label>役職</label>
                            <input type="text" value={userEditForm.position} onChange={(e) => setUserEditForm({ ...userEditForm, position: e.target.value })} placeholder="役職を入力" disabled={isSavingUser} />
                          </div>
                          <div className="form-group">
                            <label>電話番号</label>
                            <input type="tel" value={userEditForm.phone} onChange={(e) => setUserEditForm({ ...userEditForm, phone: e.target.value })} placeholder="電話番号を入力" disabled={isSavingUser} />
                          </div>
                          <div className="form-group">
                            <label>入社日</label>
                            <input type="date" value={userEditForm.join_date} onChange={(e) => setUserEditForm({ ...userEditForm, join_date: e.target.value })} disabled={isSavingUser} />
                          </div>
                        </div>
                        <div className="user-detail-edit-actions">
                          <button
                            className="dialog-cancel"
                            onClick={() => { setEditingUser(null); setIsEditingUserDetail(false); }}
                            disabled={isSavingUser}
                          >
                            キャンセル
                          </button>
                          <button className="dialog-save" onClick={handleSaveUser} disabled={isSavingUser}>
                            {isSavingUser ? '保存中...' : '💾 保存'}
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="info-grid">
                        {[
                          ['ユーザー名', selectedUser.username],
                          ['表示名', selectedUser.display_name],
                          ['姓', selectedUser.last_name],
                          ['名', selectedUser.first_name],
                          ['メールアドレス', selectedUser.email],
                          ['役割', getRoleBadge(selectedUser.role)],
                          ['部署', selectedUser.department],
                          ['役職', selectedUser.position],
                          ['電話番号', selectedUser.phone],
                          ['入社日', formatDate(selectedUser.join_date)],
                          ['登録日', formatDate(selectedUser.created_at)],
                          ['最終ログイン', selectedUser.last_login_at ? formatDateTime(selectedUser.last_login_at) : '-'],
                          ['初期ユーザ', <input type="checkbox" checked={!!selectedUser.is_initial_user} readOnly className="initial-user-checkbox" />],
                        ].map(([label, val]) => (
                          <div key={label} className="info-item">
                            <div className="info-label">{label}</div>
                            <div className="info-value">{val || '-'}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {mfaResetResult === 'success' && (
                      <div className="mfa-reset-result mfa-reset-result--success">
                        ✅ MFAをリセットしました。次回ログイン時に再設定が求められます。
                      </div>
                    )}
                    {mfaResetResult === 'success_admin' && (
                      <div className="mfa-reset-result mfa-reset-result--success">
                        ✅ 次回ログイン時にパスキーの再設定を求める状態にしました。<br />
                        ※ このユーザーが既に登録済みのパスキー自体は、Cognito側の仕様上取り消せません。
                        端末を失くした場合は、ご本人に設定画面からパスキーの削除を依頼してください。
                      </div>
                    )}
                    {mfaResetResult === 'error' && (
                      <div className="mfa-reset-result mfa-reset-result--error">
                        ❌ MFAリセットに失敗しました。もう一度お試しください。
                      </div>
                    )}

                    {canViewHistory && (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '28px' }}>
                          <h3 style={{ margin: 0 }}>🔒 ログイン履歴</h3>
                          <button
                            className="edit-button"
                            onClick={() => downloadLoginHistoryCsv(selectedUserLoginHistory, `login_history_${selectedUser.username || selectedUser.user_id}`)}
                            disabled={selectedUserLoginHistory.length === 0}
                          >
                            ⬇ CSVダウンロード
                          </button>
                        </div>
                        {selectedUserLoginHistoryLoading ? (
                          <div className="loading-container"><div className="loading-spinner"></div><p>読み込み中...</p></div>
                        ) : selectedUserLoginHistory.length === 0 ? (
                          <p style={{ color: '#9CA3AF', padding: '16px 0' }}>ログイン履歴はありません</p>
                        ) : (
                          <>
                            <p className="history-note">
                              直近20件を表示しています（過去6ヶ月・全{selectedUserLoginHistory.length}件）。全件はCSVダウンロードでご確認ください。
                            </p>
                            <div className="history-table-container">
                              <table className="history-table">
                                <thead>
                                  <tr>
                                    <th>日時</th>
                                    <th>ユーザー名</th>
                                    <th>IPアドレス</th>
                                    <th>ブラウザ</th>
                                    <th>User-Agent</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedUserLoginHistory.slice(0, 20).map(h => (
                                    <tr key={h.id}>
                                      <td className="history-datetime">{formatDateTime(h.logged_in_at)}</td>
                                      <td style={{ fontSize: '0.85rem', fontWeight: 500 }}>{h.username || h.display_name || '-'}</td>
                                      <td className="history-ip">{h.ip_address || '-'}</td>
                                      <td><span className="history-browser-badge">{h.browser || '-'}</span></td>
                                      <td className="history-ua" title={h.user_agent}>{h.user_agent ? h.user_agent.substring(0, 60) + (h.user_agent.length > 60 ? '…' : '') : '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            }

            // ソート
            const sortedUsers = [...users].sort((a, b) => {
              const va = (a[sortField] || '').toString().toLowerCase();
              const vb = (b[sortField] || '').toString().toLowerCase();
              return sortDir === 'asc' ? va.localeCompare(vb, 'ja') : vb.localeCompare(va, 'ja');
            });
            // ページネーション
            const totalPages = Math.max(1, Math.ceil(sortedUsers.length / PAGE_SIZE));
            const pagedUsers = sortedUsers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

            const maxUsers = tenantInfo?.max_users || 3;
            const currentUsers = tenantInfo?.current_users || users.length;
            const usageRatio = Math.min(1, currentUsers / maxUsers);
            const isAtLimit = currentUsers >= maxUsers;

            return (
            <div className="settings-section">
              <div className="section-header">
                <h2>👥 ユーザー一覧</h2>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="edit-button" onClick={() => { setAddUserForm({ username: '', email: '', display_name: '', role: 'user', department: '', position: '', phone: '' }); setAddUserError(''); setIsAddUserOpen(true); }}>➕ 新規ユーザー</button>
                    <button className="edit-button" onClick={() => { setUserCsvFile(null); setUserCsvPreview([]); setUserCsvError(''); setImportProgress({ done: 0, total: 0, errors: [] }); setIsUserCsvImportOpen(true); }}>📥 CSVインポート</button>
                  </div>
                )}
              </div>

              {/* ユーザー数ゲージ */}
              {tenantInfo && (
                <div style={{ margin: '0 0 16px', padding: '12px 16px', background: isAtLimit ? '#FEF2F2' : '#F0F9FF', borderRadius: '10px', border: `1px solid ${isAtLimit ? '#FECACA' : '#BAE6FD'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: isAtLimit ? '#DC2626' : '#0369A1' }}>
                      👥 ユーザー使用状況
                    </span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 700, color: isAtLimit ? '#DC2626' : '#0C4A6E' }}>
                      {currentUsers} / {maxUsers} 人
                    </span>
                  </div>
                  <div style={{ height: '8px', background: '#E5E7EB', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${usageRatio * 100}%`, background: isAtLimit ? '#EF4444' : usageRatio >= 0.8 ? '#F59E0B' : '#3B82F6', borderRadius: '4px', transition: 'width 0.4s' }} />
                  </div>
                  <p style={{ fontSize: '0.78rem', color: isAtLimit ? '#DC2626' : '#6B7280', marginTop: '6px', marginBottom: 0 }}>
                    {isAtLimit
                      ? `上限に達しています。プラン・課金でアップグレードしてください。`
                      : `あと ${maxUsers - currentUsers} 人追加できます（${tenantInfo.subscription_status === 'active' ? '有料プラン' : '無料プラン'}）`}
                  </p>
                </div>
              )}

              <div className="section-content">
                {loadingUsers ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>読み込み中...</p>
                  </div>
                ) : users.length === 0 ? (
                  <div className="empty-state">
                    <p>👥 ユーザーが見つかりません</p>
                  </div>
                ) : (
                  <>
                    <div className="users-table-meta">
                      <span className="users-total-count">{users.length}名</span>
                      {totalPages > 1 && (
                        <span className="users-page-info">{currentPage} / {totalPages} ページ</span>
                      )}
                    </div>
                    <div className="users-table-container">
                      <table className="users-table">
                        <thead>
                          <tr>
                            <th className="sortable-th" onClick={() => handleSort('username')}>
                              ユーザー名 <SortIcon field="username" />
                            </th>
                            <th className="sortable-th" onClick={() => handleSort('last_name')}>
                              姓 <SortIcon field="last_name" />
                            </th>
                            <th className="sortable-th" onClick={() => handleSort('first_name')}>
                              名 <SortIcon field="first_name" />
                            </th>
                            <th className="sortable-th" onClick={() => handleSort('email')}>
                              メールアドレス <SortIcon field="email" />
                            </th>
                            <th className="sortable-th" onClick={() => handleSort('role')}>
                              役割 <SortIcon field="role" />
                            </th>
                            <th className="sortable-th" onClick={() => handleSort('department')}>
                              部署 <SortIcon field="department" />
                            </th>
                            <th className="sortable-th" onClick={() => handleSort('position')}>
                              役職 <SortIcon field="position" />
                            </th>
                            <th style={{ textAlign: 'center' }}>初期ユーザ</th>
                            <th style={{ textAlign: 'center' }}>凍結</th>
                            {isAdmin && <th>操作</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {pagedUsers.map((u) => (
                            <tr key={u.user_id} style={u.is_frozen ? { opacity: 0.6, background: '#F3F4F6' } : {}}>
                              <td>
                                <button
                                  className="username-link"
                                  onClick={() => openUserDetail(u)}
                                >
                                  {u.username || '-'}
                                </button>
                              </td>
                              <td>{u.last_name || '-'}</td>
                              <td>{u.first_name || '-'}</td>
                              <td>{u.email || '-'}</td>
                              <td>{getRoleBadge(u.role)}</td>
                              <td>{u.department || '-'}</td>
                              <td>{u.position || '-'}</td>
                              <td style={{ textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={!!u.is_initial_user}
                                  readOnly
                                  className="initial-user-checkbox"
                                />
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={!!u.is_frozen}
                                  readOnly
                                  className="initial-user-checkbox"
                                />
                              </td>
                              {isAdmin && (
                                <td>
                                  <button
                                    className="edit-button"
                                    style={{ padding: '4px 12px', fontSize: '0.82rem' }}
                                    onClick={() => handleOpenUserEdit(u)}
                                  >
                                    ✏️ 編集
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* ページネーション */}
                    {totalPages > 1 && (
                      <div className="pagination">
                        <button
                          className="page-btn"
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                        >«</button>
                        <button
                          className="page-btn"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >‹</button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(p => Math.abs(p - currentPage) <= 2)
                          .map(p => (
                            <button
                              key={p}
                              className={`page-btn${p === currentPage ? ' page-btn--active' : ''}`}
                              onClick={() => setCurrentPage(p)}
                            >{p}</button>
                          ))}
                        <button
                          className="page-btn"
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        >›</button>
                        <button
                          className="page-btn"
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage === totalPages}
                        >»</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            );
          })()}
          {activeSection === 'assignments' && isAdmin && (
            <div className="settings-section">
              <div className="section-header">
                <h2>📌 タスク割り当て</h2>
              </div>
              <div className="section-content">
                {/* 日付選択 */}
                <div className="assignment-date-row">
                  <label className="assignment-date-label">対象日</label>
                  <input
                    type="date"
                    value={assignmentDate}
                    onChange={e => {
                      setAssignmentDate(e.target.value);
                      fetchAssignments(e.target.value);
                    }}
                    className="assignment-date-input"
                  />
                </div>

                {/* 新規割り当てフォーム */}
                <div className="assignment-form">
                  <h3>割り当て追加</h3>
                  <div className="assignment-form-row">
                    <select
                      value={assignmentForm.user_id}
                      onChange={e => setAssignmentForm({ ...assignmentForm, user_id: e.target.value })}
                      className="assignment-select"
                    >
                      <option value="">ユーザーを選択</option>
                      {assignmentUsers.map(u => (
                        <option key={u.user_id} value={u.user_id}>
                          {u.display_name || u.username || u.email}
                        </option>
                      ))}
                    </select>
                    <select
                      value={assignmentForm.task_id}
                      onChange={e => setAssignmentForm({ ...assignmentForm, task_id: e.target.value })}
                      className="assignment-select"
                    >
                      <option value="">タスクを選択</option>
                      {assignmentTasks.map(t => (
                        <option key={t.task_id} value={t.task_id}>{t.task_name}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="1"
                      max="9999"
                      value={assignmentForm.assigned_count}
                      onChange={e => setAssignmentForm({ ...assignmentForm, assigned_count: e.target.value })}
                      className="assignment-count-input"
                      placeholder="件数"
                    />
                    <button
                      className="assignment-save-btn"
                      onClick={handleSaveAssignment}
                      disabled={savingAssignment}
                    >
                      {savingAssignment ? '保存中...' : '＋ 追加'}
                    </button>
                  </div>
                </div>

                {/* 割り当て一覧 */}
                {loadingAssignments ? (
                  <div className="loading">読み込み中...</div>
                ) : assignments.length === 0 ? (
                  <p className="assignment-empty">{assignmentDate} の割り当てはまだありません</p>
                ) : (
                  <div className="user-table-wrap">
                    <table className="user-table assignment-table">
                      <thead>
                        <tr>
                          <th>ユーザー</th>
                          <th>タスク</th>
                          <th>割り当て数</th>
                          <th>操作</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignments.map(a => (
                          <tr key={a.assignment_id}>
                            <td>{a.user_display_name || a.user_email}</td>
                            <td>{a.task_name}</td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>{a.assigned_count}</td>
                            <td>
                              <button
                                className="edit-button"
                                style={{ padding: '4px 10px', fontSize: '0.8rem', background: '#FEE2E2', color: '#DC2626', border: '1px solid #FECACA' }}
                                onClick={() => handleDeleteAssignment(a.assignment_id)}
                              >
                                🗑 削除
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === 'loginHistory' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>🔒 ログイン履歴</h2>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="edit-button"
                    onClick={() => downloadLoginHistoryCsv(orgLoginHistory, 'login_history_all')}
                    disabled={orgLoginHistory.length === 0}
                  >
                    ⬇ CSVダウンロード
                  </button>
                  <button className="edit-button" onClick={fetchOrgLoginHistory}>更新</button>
                </div>
              </div>
              <div className="section-content">
                {orgLoginHistoryLoading ? (
                  <div className="loading-container"><div className="loading-spinner"></div><p>読み込み中...</p></div>
                ) : orgLoginHistory.length === 0 ? (
                  <div className="empty-state"><p>ログイン履歴はありません</p></div>
                ) : (
                  <>
                    <p className="history-note">
                      直近20件を表示しています（過去6ヶ月・全{orgLoginHistory.length}件）。全件はCSVダウンロードでご確認ください。
                    </p>
                    <div className="history-table-container">
                      <table className="history-table">
                        <thead>
                          <tr>
                            <th>日時</th>
                            <th>ユーザー名</th>
                            <th>IPアドレス</th>
                            <th>ブラウザ</th>
                            <th>User-Agent</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orgLoginHistory.slice(0, 20).map(h => (
                            <tr key={h.id}>
                              <td className="history-datetime">{formatDateTime(h.logged_in_at)}</td>
                              <td style={{ fontSize: '0.85rem', fontWeight: 500 }}>{h.username || h.display_name || '-'}</td>
                              <td className="history-ip">{h.ip_address || '-'}</td>
                              <td><span className="history-browser-badge">{h.browser || '-'}</span></td>
                              <td className="history-ua" title={h.user_agent}>{h.user_agent ? h.user_agent.substring(0, 60) + (h.user_agent.length > 60 ? '…' : '') : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {activeSection === 'auditLog' && isAdmin && (
            <div className="settings-section">
              <div className="section-header">
                <h2>📋 組織変更履歴</h2>
                <button className="edit-button" onClick={fetchAuditLog}>更新</button>
              </div>
              <div className="section-content">
                {auditLogLoading ? (
                  <div className="loading-container"><div className="loading-spinner"></div><p>読み込み中...</p></div>
                ) : auditLog.length === 0 ? (
                  <div className="empty-state"><p>変更履歴はありません</p></div>
                ) : (
                  <div className="audit-table-wrap">
                    <table className="audit-table">
                      <thead>
                        <tr>
                          <th className="audit-th-time">時刻</th>
                          <th className="audit-th-title">変更タイトル</th>
                          <th className="audit-th-target">変更項目</th>
                          <th className="audit-th-field">サブ項目</th>
                          <th className="audit-th-user">変更者</th>
                          <th className="audit-th-before">変更前</th>
                          <th className="audit-th-after">変更後</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLog.flatMap(entry => {
                          const changes = entry.changes && Object.keys(entry.changes).length > 0
                            ? Object.entries(entry.changes)
                            : null;
                          if (!changes) {
                            return [(
                              <tr key={entry.id} className={`audit-row audit-row--${entry.action} audit-row--last`}>
                                <td className="audit-col-time">{formatDateTime(entry.occurred_at)}</td>
                                <td className="audit-col-title">
                                  <div className="audit-title-inner">
                                    <span className={`audit-badge audit-badge--${entry.action}`}>{getActionLabel(entry.action)}</span>
                                  </div>
                                </td>
                                <td className="audit-col-target">{entry.target_display_name || '-'}</td>
                                <td className="audit-col-field">-</td>
                                <td className="audit-col-user">{entry.user_display_name || '-'}</td>
                                <td className="audit-col-before">-</td>
                                <td className="audit-col-after">-</td>
                              </tr>
                            )];
                          }
                          return changes.map(([field, diff], idx) => (
                            <tr key={`${entry.id}-${field}`} className={`audit-row audit-row--${entry.action}${idx > 0 ? ' audit-row--cont' : ''}${idx === changes.length - 1 ? ' audit-row--last' : ''}`}>
                              <td className="audit-col-time">{idx === 0 ? formatDateTime(entry.occurred_at) : ''}</td>
                              <td className="audit-col-title">
                                <div className="audit-title-inner">
                                  {idx === 0 && <span className={`audit-badge audit-badge--${entry.action}`}>{getActionLabel(entry.action)}</span>}
                                </div>
                              </td>
                              <td className="audit-col-target">{idx === 0 ? (entry.target_display_name || '-') : ''}</td>
                              <td className="audit-col-field">
                                <span className="audit-field-tag">{getFieldLabel(field)}</span>
                              </td>
                              <td className="audit-col-user">{idx === 0 ? (entry.user_display_name || '-') : ''}</td>
                              <td className="audit-col-before">{diff.before || '（空）'}</td>
                              <td className="audit-col-after">{diff.after || '（空）'}</td>
                            </tr>
                          ));
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === 'ipRestrictions' && isAdmin && (
            <div className="settings-section">
              <div className="section-header">
                <h2>🔐 IPアドレス制限</h2>
              </div>
              <div className="section-content">
                <p className="ip-restriction-note">
                  ユーザーごとにアクセスを許可するIPアドレスを設定できます。制限を設定すると、登録されたIPアドレス以外からのAPIアクセスは拒否されます。
                  {currentIp && <span className="ip-current-badge">現在のIP: {currentIp}</span>}
                </p>

                {/* プリセット管理 */}
                <div className="ip-section-block">
                  <h3>プリセット管理</h3>
                  <p className="ip-section-desc">よく使うIPアドレス範囲をプリセットとして登録し、複数ユーザーに一括適用できます。</p>

                  <div className="ip-preset-form">
                    <input
                      className="ip-input"
                      placeholder="プリセット名（例：オフィス）"
                      value={ipPresetForm.name}
                      onChange={e => setIpPresetForm(p => ({ ...p, name: e.target.value }))}
                    />
                    <textarea
                      className="ip-textarea"
                      placeholder={'IPアドレス / CIDR（1行ずつ）\n例:\n192.168.1.0/24\n10.0.0.1'}
                      value={ipPresetForm.cidrs}
                      onChange={e => setIpPresetForm(p => ({ ...p, cidrs: e.target.value }))}
                      rows={4}
                    />
                    {ipPresetError && <div className="form-error">{ipPresetError}</div>}
                    <div className="ip-preset-form-actions">
                      {editingPreset && <button className="edit-button" onClick={() => { setEditingPreset(null); setIpPresetForm({ name: '', cidrs: '' }); }}>キャンセル</button>}
                      <button className="dialog-save" onClick={saveIpPreset}>
                        {editingPreset ? '更新' : 'プリセット追加'}
                      </button>
                    </div>
                  </div>

                  {ipPresetsLoading ? (
                    <p>読み込み中...</p>
                  ) : ipPresets.length === 0 ? (
                    <p className="ip-empty">プリセットはまだありません</p>
                  ) : (
                    <div className="ip-preset-list">
                      {ipPresets.map(preset => (
                        <div key={preset.id} className="ip-preset-item">
                          <div className="ip-preset-info">
                            <span className="ip-preset-name">{preset.name}</span>
                            <span className="ip-preset-cidrs">{preset.cidrs.join(', ')}</span>
                          </div>
                          <div className="ip-preset-actions">
                            <button className="ip-btn-secondary" onClick={() => setBulkApplyDialog(preset)}>一括適用</button>
                            <button className="ip-btn-secondary" onClick={() => { setEditingPreset(preset); setIpPresetForm({ name: preset.name, cidrs: preset.cidrs.join('\n') }); }}>編集</button>
                            <button className="ip-btn-danger" onClick={() => deleteIpPreset(preset.id)}>削除</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ユーザーごとのIP制限 */}
                <div className="ip-section-block">
                  <h3>ユーザー別IP制限</h3>
                  {users.length === 0 ? (
                    <p className="ip-empty">ユーザーを先に読み込んでください</p>
                  ) : (
                    <div className="ip-user-list">
                      {users.map(u => {
                        const restrictions = userIpRestrictions[u.user_id];
                        const isLoaded = restrictions !== undefined;
                        const hasRestrictions = isLoaded && restrictions.length > 0;
                        const warn = hasRestrictions && checkCurrentIpWarning(restrictions);
                        return (
                          <div key={u.user_id} className={`ip-user-item ${warn ? 'ip-warn' : ''}`}>
                            <div className="ip-user-header">
                              <span className="ip-user-name">{u.display_name || u.username}</span>
                              {hasRestrictions && <span className="ip-restriction-count">{restrictions.length}件の制限</span>}
                              {warn && <span className="ip-warn-badge">⚠️ 現在のIPが範囲外</span>}
                              {!isLoaded && (
                                <button className="ip-btn-secondary" onClick={() => fetchUserIpRestrictions(u.user_id)}>制限を表示</button>
                              )}
                            </div>
                            {isLoaded && (
                              <div className="ip-user-restrictions">
                                {restrictions.length === 0 ? (
                                  <span className="ip-no-restriction">制限なし（全IPから接続可）</span>
                                ) : (
                                  <div className="ip-restriction-list">
                                    {restrictions.map(r => (
                                      <div key={r.id} className="ip-restriction-row">
                                        <span className="ip-cidr">{r.ip_cidr}</span>
                                        {r.label && <span className="ip-label">{r.label}</span>}
                                        <button className="ip-btn-danger ip-btn-sm" onClick={() => deleteUserIpRestriction(u.user_id, r.id)}>✕</button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <div className="ip-add-form">
                                  <input className="ip-input ip-input-sm" placeholder="IPまたはCIDR（例: 192.168.1.0/24）" value={ipAddForm.ip_cidr} onChange={e => setIpAddForm(p => ({ ...p, ip_cidr: e.target.value }))} />
                                  <input className="ip-input ip-input-sm" placeholder="ラベル（任意）" value={ipAddForm.label} onChange={e => setIpAddForm(p => ({ ...p, label: e.target.value }))} />
                                  <button className="ip-btn-primary" onClick={() => addUserIpRestriction(u.user_id, ipAddForm.ip_cidr, ipAddForm.label)}>追加</button>
                                </div>
                                {ipAddError && <div className="form-error">{ipAddError}</div>}
                                {restrictions.length > 0 && (
                                  <button className="ip-btn-danger ip-btn-sm" style={{ marginTop: 4 }} onClick={async () => {
                                    if (!window.confirm('このユーザーのIP制限を全て削除しますか？')) return;
                                    const op = del({ apiName: 'worktimerApi', path: `/users/${u.user_id}/ip-restrictions` });
                                    await op.response;
                                    await fetchUserIpRestrictions(u.user_id);
                                  }}>全制限を削除</button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeSection === 'billing' && isAdmin && (
            <BillingPage user={user} onBack={() => setActiveSection('company')} onFaq={() => setActiveSection('faq')} />
          )}

          {activeSection === 'faq' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>❓ よくある質問</h2>
              </div>
              <div className="section-content">
                <div className="faq-list">

                  <div className="faq-category">
                    <h3>アカウント・ユーザー</h3>
                    <div className="faq-item">
                      <div className="faq-q">Q. パスワードを忘れました。どうすればいいですか？</div>
                      <div className="faq-a">ログイン画面の「パスワードを忘れた方はこちら」から再設定できます。登録済みのメールアドレスに再設定用のリンクが届きます。</div>
                    </div>
                    <div className="faq-item">
                      <div className="faq-q">Q. ユーザーを追加・削除するにはどうすればいいですか？</div>
                      <div className="faq-a">管理者が「設定 → ユーザー管理」からユーザーの招待・削除が可能です。招待するとメールが届き、アカウントを作成できます。</div>
                    </div>
                    <div className="faq-item">
                      <div className="faq-q">Q. メールアドレスを変更するにはどうすればいいですか？</div>
                      <div className="faq-a">「設定 → マイプロフィール → 編集」から変更できます。変更後、確認メールが届くので承認してください。</div>
                    </div>
                    <div className="faq-item">
                      <div className="faq-q">Q. 二段階認証はありますか？</div>
                      <div className="faq-a">あります。「設定 → マイプロフィール → 編集」から二段階認証（2FA）を有効にできます。新しいデバイスからログインする際にコードの入力が必要になります。</div>
                    </div>
                  </div>

                  <div className="faq-category">
                    <h3>タスク・作業記録</h3>
                    <div className="faq-item">
                      <div className="faq-q">Q. タスクはいくつまで登録できますか？</div>
                      <div className="faq-a">無料プランでは3件まで、有料プランでは無制限に登録できます。</div>
                    </div>
                    <div className="faq-item">
                      <div className="faq-q">Q. 過去の作業時間を修正することはできますか？</div>
                      <div className="faq-a">管理者は「設定 → タスク割り当て」から過去の記録を確認・修正できます。</div>
                    </div>
                    <div className="faq-item">
                      <div className="faq-q">Q. タイマーをうっかり止め忘れた場合はどうなりますか？</div>
                      <div className="faq-a">記録された時間はそのまま保持されます。管理者に連絡して修正してもらうか、次回の記録で調整してください。</div>
                    </div>
                  </div>

                  <div className="faq-category">
                    <h3>プラン・課金</h3>
                    <div className="faq-item">
                      <div className="faq-q">Q. 無料プランと有料プランの違いは何ですか？</div>
                      <div className="faq-a">無料プランはユーザー3名・タスク3件までの制限があります。有料プランはタスク数が無制限で、ユーザー数はご利用人数に合わせて設定します。</div>
                    </div>
                    <div className="faq-item">
                      <div className="faq-q">Q. 有料プランの料金・申し込み方法を教えてください。</div>
                      <div className="faq-a">料金のご案内・お申し込みは、すべてお問い合わせにて承っております。「設定 → プラン・課金」のお問い合わせフォームからご連絡ください。担当者よりご案内いたします。</div>
                    </div>
                    <div className="faq-item">
                      <div className="faq-q">Q. ユーザー数を変更したい場合はどうすればいいですか？</div>
                      <div className="faq-a">「設定 → プラン・課金」のお問い合わせフォームから、ご希望の人数をお知らせください。</div>
                    </div>
                    <div className="faq-item">
                      <div className="faq-q">Q. 解約はいつでもできますか？</div>
                      <div className="faq-a">はい。「設定 → プラン・課金」のお問い合わせフォームから解約をご希望の旨をお知らせください。</div>
                    </div>
                    <div className="faq-item">
                      <div className="faq-q">Q. 請求書・領収書はもらえますか？</div>
                      <div className="faq-a">はい。「設定 → プラン・課金」のお問い合わせフォームからご依頼ください。</div>
                    </div>
                  </div>

                  <div className="faq-category">
                    <h3>セキュリティ・プライバシー</h3>
                    <div className="faq-item">
                      <div className="faq-q">Q. データはどこに保存されていますか？</div>
                      <div className="faq-a">AWSのデータセンター（東京リージョン）に保存されています。通信はすべてSSL/TLSで暗号化されています。</div>
                    </div>
                    <div className="faq-item">
                      <div className="faq-q">Q. 退会するとデータはどうなりますか？</div>
                      <div className="faq-a">解約後30日間はデータを保持します。その後、すべてのデータは完全に削除されます。必要な場合は解約前にエクスポートしてください。</div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

      {/* プロフィール編集ダイアログ */}
      {isEditDialogOpen && (
        <div className="dialog-overlay">
          <div className="dialog">
            <div className="dialog-header">
              <h3>✏️ プロフィール編集</h3>
              <button className="dialog-close" onClick={() => setIsEditDialogOpen(false)}>✕</button>
            </div>
            <div className="dialog-body">
              <div className="form-group">
                <label>姓</label>
                <input
                  type="text"
                  value={editForm.lastName}
                  onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                  placeholder="姓を入力"
                />
              </div>
              <div className="form-group">
                <label>名</label>
                <input
                  type="text"
                  value={editForm.firstName}
                  onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                  placeholder="名を入力"
                />
              </div>
              <div className="form-group">
                <label>ユーザー名</label>
                <input
                  type="text"
                  value={editForm.username}
                  onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  placeholder="ユーザー名を入力"
                />
              </div>
              <div className="form-group">
                <label>表示名</label>
                <input
                  type="text"
                  value={editForm.displayName}
                  onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                  placeholder="表示名を入力"
                />
              </div>
              <div className="form-group">
                <label>部署</label>
                <input
                  type="text"
                  value={editForm.department}
                  onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                  placeholder="部署を入力"
                />
              </div>
              <div className="form-group">
                <label>役職</label>
                <input
                  type="text"
                  value={editForm.position}
                  onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
                  placeholder="役職を入力"
                />
              </div>
              <div className="form-group">
                <label>電話番号</label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="電話番号を入力"
                />
              </div>
              <div className="form-group">
                <label>メールアドレス変更</label>
                <button
                  type="button"
                  className="edit-button"
                  style={{ width: '100%', textAlign: 'center' }}
                  onClick={() => { setIsEditDialogOpen(false); setEmailChangeStep(1); setNewEmail(''); setEmailChangeCode(''); setEmailChangeError(''); setIsEmailChangeOpen(true); }}
                >
                  ✉️ メールアドレスを変更する
                </button>
              </div>
            </div>
            <div className="dialog-footer">
              <button className="dialog-cancel" onClick={() => setIsEditDialogOpen(false)} disabled={isSavingProfile}>キャンセル</button>
              <button className="dialog-save" onClick={handleSaveProfile} disabled={isSavingProfile}>
                {isSavingProfile ? '保存中...' : '💾 保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* メールアドレス変更ダイアログ */}
      {isEmailChangeOpen && (
        <div className="dialog-overlay">
          <div className="dialog">
            <div className="dialog-header">
              <h3>✉️ メールアドレス変更</h3>
              <button className="dialog-close" onClick={() => setIsEmailChangeOpen(false)} disabled={isChangingEmail}>✕</button>
            </div>
            <div className="dialog-body">
              {emailChangeError && <div style={{ color: '#EF4444', fontSize: '0.9rem', marginBottom: '12px' }}>{emailChangeError}</div>}
              {emailChangeStep === 1 ? (
                <>
                  <p style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '12px' }}>
                    新しいメールアドレスに確認コードを送信します。
                  </p>
                  <div className="form-group">
                    <label>新しいメールアドレス</label>
                    <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="new@example.com" disabled={isChangingEmail} />
                  </div>
                  <button className="dialog-save" style={{ width: '100%' }} onClick={handleSendEmailChangeCode} disabled={isChangingEmail || !newEmail}>
                    {isChangingEmail ? '送信中...' : '確認コードを送信'}
                  </button>
                </>
              ) : (
                <>
                  <p style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '12px' }}>
                    <strong>{newEmail}</strong> に送信された確認コードを入力してください。
                  </p>
                  <div className="form-group">
                    <label>確認コード</label>
                    <input type="text" value={emailChangeCode} onChange={(e) => setEmailChangeCode(e.target.value)} placeholder="6桁のコード" disabled={isChangingEmail} />
                  </div>
                  <button className="dialog-save" style={{ width: '100%' }} onClick={handleConfirmEmailChange} disabled={isChangingEmail || !emailChangeCode}>
                    {isChangingEmail ? '確認中...' : 'メールアドレスを変更する'}
                  </button>
                  <button type="button" style={{ width: '100%', marginTop: '8px', background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: '0.85rem' }} onClick={() => setEmailChangeStep(1)}>
                    ← メールアドレスを変更する
                  </button>
                </>
              )}
            </div>
            <div className="dialog-footer">
              <button className="dialog-cancel" onClick={() => setIsEmailChangeOpen(false)} disabled={isChangingEmail}>閉じる</button>
            </div>
          </div>
        </div>
      )}

      {/* 会社概要編集ダイアログ */}
      {isCompanyEditOpen && (
        <div className="dialog-overlay">
          <div className="dialog">
            <div className="dialog-header">
              <h3>🏢 会社概要編集</h3>
              <button className="dialog-close" onClick={() => setIsCompanyEditOpen(false)}>✕</button>
            </div>
            <div className="dialog-body">
              <div className="form-group">
                <label>会社名</label>
                <input type="text" value={companyForm.company_name} onChange={(e) => setCompanyForm({ ...companyForm, company_name: e.target.value })} placeholder="会社名を入力" disabled={isSavingCompany} />
              </div>
              <div className="form-group">
                <label>所在地</label>
                <input type="text" value={companyForm.address} onChange={(e) => setCompanyForm({ ...companyForm, address: e.target.value })} placeholder="所在地を入力" disabled={isSavingCompany} />
              </div>
              <div className="form-group">
                <label>電話番号</label>
                <input type="tel" value={companyForm.phone} onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })} placeholder="電話番号を入力" disabled={isSavingCompany} />
              </div>
              <div className="form-group">
                <label>FAX</label>
                <input type="tel" value={companyForm.fax} onChange={(e) => setCompanyForm({ ...companyForm, fax: e.target.value })} placeholder="FAXを入力" disabled={isSavingCompany} />
              </div>
              <div className="form-group">
                <label>セッションタイムアウト</label>
                <select
                  value={companyForm.session_timeout_minutes}
                  onChange={(e) => setCompanyForm({ ...companyForm, session_timeout_minutes: parseInt(e.target.value, 10) })}
                  disabled={isSavingCompany}
                >
                  <option value={15}>15分</option>
                  <option value={30}>30分</option>
                  <option value={60}>1時間</option>
                  <option value={120}>2時間</option>
                  <option value={480}>8時間</option>
                </select>
              </div>
            </div>
            <div className="dialog-footer">
              <button className="dialog-cancel" onClick={() => setIsCompanyEditOpen(false)} disabled={isSavingCompany}>キャンセル</button>
              <button className="dialog-save" onClick={handleSaveCompany} disabled={isSavingCompany}>
                {isSavingCompany ? '保存中...' : '💾 保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ユーザーCSVインポートダイアログ */}
      {isUserCsvImportOpen && (
        <div className="dialog-overlay">
          <div className="dialog" style={{ maxWidth: '560px' }}>
            <div className="dialog-header">
              <h3>📥 ユーザーCSVインポート</h3>
              <button className="dialog-close" onClick={() => setIsUserCsvImportOpen(false)} disabled={isImportingUsers}>✕</button>
            </div>
            <div className="dialog-body">
              <p style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '12px' }}>
                CSVの1行目はヘッダー行にしてください。<br />
                必須列: <code>email</code>, <code>display_name</code><br />
                任意列: <code>username</code>, <code>role</code>(user/admin), <code>department</code>, <code>position</code>, <code>phone</code>
              </p>
              <div className="form-group">
                <label>CSVファイル</label>
                <input type="file" accept=".csv,text/csv" onChange={handleUserCsvFile} disabled={isImportingUsers} />
              </div>
              {userCsvError && <div style={{ color: '#EF4444', fontSize: '0.9rem', marginTop: '8px' }}>{userCsvError}</div>}
              {userCsvPreview.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <p style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>プレビュー（最大5件）</p>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#F3F4F6' }}>
                          {Object.keys(userCsvPreview[0]).map(k => <th key={k} style={{ padding: '4px 8px', textAlign: 'left', border: '1px solid #E5E7EB' }}>{k}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {userCsvPreview.map((row, i) => (
                          <tr key={i}>
                            {Object.values(row).map((v, j) => <td key={j} style={{ padding: '4px 8px', border: '1px solid #E5E7EB' }}>{v}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {isImportingUsers && (
                <div style={{ marginTop: '12px' }}>
                  <p style={{ fontSize: '0.9rem' }}>{importProgress.done} / {importProgress.total} 件処理中...</p>
                  <div style={{ height: '8px', background: '#E5E7EB', borderRadius: '4px', marginTop: '6px' }}>
                    <div style={{ height: '100%', background: '#3B82F6', borderRadius: '4px', width: `${importProgress.total ? (importProgress.done / importProgress.total) * 100 : 0}%`, transition: 'width 0.3s' }} />
                  </div>
                </div>
              )}
              {!isImportingUsers && importProgress.errors.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <p style={{ fontSize: '0.85rem', color: '#EF4444', fontWeight: 600 }}>エラーが発生した行:</p>
                  <ul style={{ fontSize: '0.82rem', color: '#EF4444', paddingLeft: '16px' }}>
                    {importProgress.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
            <div className="dialog-footer">
              <button className="dialog-cancel" onClick={() => setIsUserCsvImportOpen(false)} disabled={isImportingUsers}>キャンセル</button>
              <button className="dialog-save" onClick={handleImportUsers} disabled={isImportingUsers || !userCsvFile || !!userCsvError}>
                {isImportingUsers ? 'インポート中...' : '📥 インポート開始'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 新規ユーザー追加ダイアログ */}
      {isAddUserOpen && (
        <div className="dialog-overlay">
          <div className="dialog">
            <div className="dialog-header">
              <h3>➕ 新規ユーザー追加</h3>
              <button className="dialog-close" onClick={() => setIsAddUserOpen(false)}>✕</button>
            </div>
            <div className="dialog-body">
              {addUserError && <div style={{ color: '#EF4444', marginBottom: '12px', fontSize: '0.9rem' }}>{addUserError}</div>}
              <div className="form-group">
                <label>ユーザー名 <span style={{ color: '#EF4444' }}>*</span></label>
                <input type="text" value={addUserForm.username} onChange={(e) => setAddUserForm({ ...addUserForm, username: e.target.value })} placeholder="yamada_taro" disabled={isAddingUser} />
                <p style={{ fontSize: '0.78rem', color: '#6B7280', marginTop: '4px', marginBottom: 0 }}>
                  ※ ログイン時に使用します。システム全体で唯一である必要があります
                </p>
              </div>
              <div className="form-group">
                <label>メールアドレス <span style={{ color: '#EF4444' }}>*</span></label>
                <input type="email" value={addUserForm.email} onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })} placeholder="user@example.com" disabled={isAddingUser} />
              </div>
              <div className="form-group">
                <label>表示名 <span style={{ color: '#EF4444' }}>*</span></label>
                <input type="text" value={addUserForm.display_name} onChange={(e) => setAddUserForm({ ...addUserForm, display_name: e.target.value })} placeholder="山田 太郎" disabled={isAddingUser} />
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>姓</label>
                  <input type="text" value={addUserForm.last_name} onChange={(e) => setAddUserForm({ ...addUserForm, last_name: e.target.value })} placeholder="山田" disabled={isAddingUser} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>名</label>
                  <input type="text" value={addUserForm.first_name} onChange={(e) => setAddUserForm({ ...addUserForm, first_name: e.target.value })} placeholder="太郎" disabled={isAddingUser} />
                </div>
              </div>
              <div className="form-group">
                <label>役割</label>
                <select value={addUserForm.role} onChange={(e) => setAddUserForm({ ...addUserForm, role: e.target.value })} disabled={isAddingUser}>
                  <option value="user">一般ユーザー</option>
                  <option value="admin">管理者</option>
                </select>
              </div>
              <div className="form-group">
                <label>部署</label>
                <input type="text" value={addUserForm.department} onChange={(e) => setAddUserForm({ ...addUserForm, department: e.target.value })} placeholder="営業部" disabled={isAddingUser} />
              </div>
              <div className="form-group">
                <label>役職</label>
                <input type="text" value={addUserForm.position} onChange={(e) => setAddUserForm({ ...addUserForm, position: e.target.value })} placeholder="主任" disabled={isAddingUser} />
              </div>
              <div className="form-group">
                <label>電話番号</label>
                <input type="tel" value={addUserForm.phone} onChange={(e) => setAddUserForm({ ...addUserForm, phone: e.target.value })} placeholder="03-1234-5678" disabled={isAddingUser} />
              </div>
              <p style={{ fontSize: '0.82rem', color: '#6B7280', marginTop: '8px' }}>
                ※ 仮パスワードが自動生成され、ユーザーが初回ログイン時に変更します
              </p>
            </div>
            <div className="dialog-footer">
              <button className="dialog-cancel" onClick={() => setIsAddUserOpen(false)} disabled={isAddingUser}>キャンセル</button>
              <button className="dialog-save" onClick={handleAddUser} disabled={isAddingUser}>
                {isAddingUser ? '追加中...' : '➕ 追加する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ユーザー作成完了・仮パスワード表示ダイアログ */}
      {createdUserInfo && (
        <div className="dialog-overlay">
          <div className="dialog" style={{ maxWidth: '460px' }}>
            <div className="dialog-header">
              <h3>✅ ユーザーを作成しました</h3>
            </div>
            <div className="dialog-body">
              <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '16px' }}>
                以下の情報をユーザーに共有してください。<br />
                <strong style={{ color: '#EF4444' }}>このダイアログを閉じると仮パスワードは再表示されません。</strong>
              </p>
              <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px', fontSize: '0.9rem' }}>
                <div style={{ marginBottom: '10px' }}>
                  <span style={{ color: '#6B7280', fontSize: '0.8rem' }}>表示名</span>
                  <div style={{ fontWeight: 600 }}>{createdUserInfo.display_name}</div>
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <span style={{ color: '#6B7280', fontSize: '0.8rem' }}>ユーザー名（ログイン時に使用）</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <code style={{ fontWeight: 700, fontSize: '1rem', background: '#EFF6FF', padding: '4px 8px', borderRadius: '4px' }}>{createdUserInfo.username}</code>
                    <button className="edit-button" style={{ padding: '2px 10px', fontSize: '0.78rem' }}
                      onClick={() => navigator.clipboard.writeText(createdUserInfo.username)}>コピー</button>
                  </div>
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <span style={{ color: '#6B7280', fontSize: '0.8rem' }}>メールアドレス</span>
                  <div>{createdUserInfo.email}</div>
                </div>
                <div>
                  <span style={{ color: '#6B7280', fontSize: '0.8rem' }}>仮パスワード（初回ログイン後に変更が必要）</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <code style={{ fontWeight: 700, fontSize: '1rem', background: '#FFF7ED', padding: '4px 8px', borderRadius: '4px', letterSpacing: '0.05em' }}>{createdUserInfo.temporary_password}</code>
                    <button className="edit-button" style={{ padding: '2px 10px', fontSize: '0.78rem' }}
                      onClick={() => navigator.clipboard.writeText(createdUserInfo.temporary_password)}>コピー</button>
                  </div>
                </div>
              </div>
            </div>
            <div className="dialog-footer">
              <button className="dialog-save" onClick={() => setCreatedUserInfo(null)}>閉じる</button>
            </div>
          </div>
        </div>
      )}

      {/* 管理者によるユーザー編集ダイアログ */}
      {editingUser && !isEditingUserDetail && (
        <div className="dialog-overlay">
          <div className="dialog">
            <div className="dialog-header">
              <h3>✏️ ユーザー編集</h3>
              <button className="dialog-close" onClick={() => setEditingUser(null)}>✕</button>
            </div>
            <div className="dialog-body">
              <div className="form-group">
                <label>姓</label>
                <input type="text" value={userEditForm.last_name} onChange={(e) => setUserEditForm({ ...userEditForm, last_name: e.target.value })} placeholder="姓を入力" disabled={isSavingUser} />
              </div>
              <div className="form-group">
                <label>名</label>
                <input type="text" value={userEditForm.first_name} onChange={(e) => setUserEditForm({ ...userEditForm, first_name: e.target.value })} placeholder="名を入力" disabled={isSavingUser} />
              </div>
              <div className="form-group">
                <label>ユーザー名</label>
                <input type="text" value={userEditForm.username} onChange={(e) => setUserEditForm({ ...userEditForm, username: e.target.value })} placeholder="ユーザー名を入力" disabled={isSavingUser} />
              </div>
              <div className="form-group">
                <label>表示名</label>
                <input type="text" value={userEditForm.display_name} onChange={(e) => setUserEditForm({ ...userEditForm, display_name: e.target.value })} placeholder="表示名を入力" disabled={isSavingUser} />
              </div>
              <div className="form-group">
                <label>役割</label>
                <select value={userEditForm.role} onChange={(e) => setUserEditForm({ ...userEditForm, role: e.target.value })} disabled={isSavingUser}>
                  <option value="user">一般ユーザー</option>
                  <option value="admin">管理者</option>
                </select>
              </div>
              <div className="form-group">
                <label>部署</label>
                <input type="text" value={userEditForm.department} onChange={(e) => setUserEditForm({ ...userEditForm, department: e.target.value })} placeholder="部署を入力" disabled={isSavingUser} />
              </div>
              <div className="form-group">
                <label>役職</label>
                <input type="text" value={userEditForm.position} onChange={(e) => setUserEditForm({ ...userEditForm, position: e.target.value })} placeholder="役職を入力" disabled={isSavingUser} />
              </div>
              <div className="form-group">
                <label>電話番号</label>
                <input type="tel" value={userEditForm.phone} onChange={(e) => setUserEditForm({ ...userEditForm, phone: e.target.value })} placeholder="電話番号を入力" disabled={isSavingUser} />
              </div>
              <div className="form-group">
                <label>入社日</label>
                <input type="date" value={userEditForm.join_date} onChange={(e) => setUserEditForm({ ...userEditForm, join_date: e.target.value })} disabled={isSavingUser} />
              </div>
            </div>
            <div className="dialog-footer">
              <button className="dialog-cancel" onClick={() => setEditingUser(null)} disabled={isSavingUser}>キャンセル</button>
              <button className="dialog-save" onClick={handleSaveUser} disabled={isSavingUser}>
                {isSavingUser ? '保存中...' : '💾 保存'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* IP制限一括適用ダイアログ */}
      {bulkApplyDialog && (
        <div className="dialog-overlay">
          <div className="dialog" style={{ maxWidth: '480px' }}>
            <div className="dialog-header">
              <h3>「{bulkApplyDialog.name}」を一括適用</h3>
              <button className="dialog-close" onClick={() => { setBulkApplyDialog(null); setBulkSelectedUsers([]); }}>✕</button>
            </div>
            <div className="dialog-body">
              <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>
                適用するIPアドレス: {bulkApplyDialog.cidrs.join(', ')}
              </p>
              <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>適用するユーザーを選択:</p>
              <div className="ip-bulk-user-list">
                {users.map(u => (
                  <label key={u.user_id} className="ip-bulk-user-row">
                    <input
                      type="checkbox"
                      checked={bulkSelectedUsers.includes(u.user_id)}
                      onChange={e => setBulkSelectedUsers(prev => e.target.checked ? [...prev, u.user_id] : prev.filter(id => id !== u.user_id))}
                    />
                    <span>{u.display_name || u.username}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="dialog-footer">
              <button className="dialog-cancel" onClick={() => { setBulkApplyDialog(null); setBulkSelectedUsers([]); }}>キャンセル</button>
              <button className="dialog-save" onClick={applyPresetBulk} disabled={bulkSelectedUsers.length === 0}>
                {bulkSelectedUsers.length}人に適用
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MFA設定ダイアログ */}
      {mfaDialogOpen && (
        <div className="dialog-overlay">
          <div className="dialog" style={{ maxWidth: '440px' }}>
            <div className="dialog-header">
              <h3>二段階認証（MFA）設定</h3>
              <button className="dialog-close" onClick={() => { setMfaDialogOpen(false); setMfaCode(''); setMfaError(''); }}>✕</button>
            </div>
            <div className="dialog-body">
              {mfaLoading && mfaStep === 'check' ? (
                <div className="loading-container"><div className="loading-spinner"></div><p>確認中...</p></div>
              ) : mfaStep === 'already_enabled' ? (
                <div className="mfa-status-block">
                  <div className="mfa-status-badge mfa-enabled">✓ MFA有効</div>
                  <p>二段階認証が有効になっています。ログイン時に認証アプリのコードが必要です。</p>
                  {mfaError && <div className="form-error">{mfaError}</div>}
                  <button className="mfa-disable-btn" onClick={disableMfa} disabled={mfaLoading}>
                    {mfaLoading ? '処理中...' : 'MFAを無効にする'}
                  </button>
                </div>
              ) : mfaStep === 'setup_intro' ? (
                <div className="mfa-status-block">
                  <div className="mfa-status-badge mfa-disabled">✗ MFA未設定</div>
                  <p>Google Authenticator や Authy などの認証アプリでQRコードをスキャンして、二段階認証を設定します。</p>
                  {mfaError && <div className="form-error">{mfaError}</div>}
                  <button className="dialog-save" onClick={startMfaSetup} disabled={mfaLoading}>
                    {mfaLoading ? '準備中...' : 'MFAを設定する'}
                  </button>
                </div>
              ) : mfaStep === 'scan' ? (
                <div>
                  <p className="mfa-step-label">① 認証アプリでQRコードをスキャン</p>
                  {mfaQrUrl && <img src={mfaQrUrl} alt="QR Code" className="mfa-qr" />}
                  <p className="mfa-step-label">スキャンできない場合はこのコードを手動入力してください：</p>
                  <div className="mfa-secret-box">{mfaSecret}</div>
                  <p className="mfa-step-label">② 認証アプリに表示された6桁のコードを入力</p>
                  <input
                    type="text"
                    className="mfa-code-input"
                    placeholder="6桁のコード"
                    value={mfaCode}
                    onChange={e => { setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setMfaError(''); }}
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                  {mfaError && <div className="form-error">{mfaError}</div>}
                </div>
              ) : mfaStep === 'done' ? (
                <div className="mfa-status-block">
                  <div className="mfa-status-badge mfa-enabled">✓ MFA設定完了</div>
                  <p>二段階認証が有効になりました。次回ログインから認証コードが必要になります。</p>
                </div>
              ) : null}
            </div>
            {(mfaStep === 'scan') && (
              <div className="dialog-footer">
                <button className="dialog-cancel" onClick={() => setMfaStep('setup_intro')}>戻る</button>
                <button className="dialog-save" onClick={verifyMfa} disabled={mfaLoading || mfaCode.length !== 6}>
                  {mfaLoading ? '確認中...' : '確認して有効化'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* パスキー管理ダイアログ（管理者用） */}
      {passkeyDialogOpen && (
        <div className="dialog-overlay">
          <div className="dialog" style={{ maxWidth: '480px' }}>
            <div className="dialog-header">
              <h3>🔑 パスキー管理</h3>
              <button className="dialog-close" onClick={() => { setPasskeyDialogOpen(false); setPasskeyError(''); }}>✕</button>
            </div>
            <div className="dialog-body">
              {passkeyError && <div className="form-error">{passkeyError}</div>}
              {passkeysLoading ? (
                <div className="loading-container"><div className="loading-spinner"></div><p>読み込み中...</p></div>
              ) : (
                <>
                  <ul className="passkey-list">
                    {passkeys.length === 0 && <li className="passkey-list-empty">登録済みのパスキーはありません</li>}
                    {passkeys.map(pk => (
                      <li key={pk.credentialId} className="passkey-list-item">
                        <div className="passkey-name">
                          <span className="passkey-name-text">{pk.friendlyCredentialName || 'パスキー'}</span>
                          <span className={`passkey-type-badge ${pk.authenticatorAttachment === 'platform' ? 'passkey-type-platform' : 'passkey-type-roaming'}`}>
                            {pk.authenticatorAttachment === 'platform'
                              ? '📱 この端末の組み込み認証（指紋・顔認証等）'
                              : '🔑 セキュリティキー / 他デバイスのパスキー'}
                          </span>
                        </div>
                        <span className="passkey-created">
                          {pk.createdAt ? new Date(pk.createdAt).toLocaleDateString('ja-JP') : ''}
                        </span>
                        <button className="passkey-delete-btn" onClick={() => setPasskeyDeleteConfirm(pk.credentialId)}>
                          削除
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button className="dialog-save" onClick={addPasskey} disabled={passkeyAdding} style={{ marginTop: '12px' }}>
                    {passkeyAdding ? '登録中...' : '＋ パスキーを追加'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* パスキー削除確認ダイアログ */}
      {passkeyDeleteConfirm && (
        <div className="dialog-overlay">
          <div className="dialog" style={{ maxWidth: '400px' }}>
            <div className="dialog-header">
              <h3>パスキーの削除</h3>
            </div>
            <div className="dialog-body">
              <p>
                {passkeys.length <= 1
                  ? 'これは最後のパスキーです。削除すると次回ログインできなくなる可能性があります。本当に削除しますか？'
                  : 'このパスキーを削除します。よろしいですか？'}
              </p>
            </div>
            <div className="dialog-footer">
              <button className="dialog-cancel" onClick={() => setPasskeyDeleteConfirm(null)}>キャンセル</button>
              <button className="mfa-disable-btn" onClick={() => removePasskey(passkeyDeleteConfirm)}>削除する</button>
            </div>
          </div>
        </div>
      )}

      {/* パスワードリセット確認ダイアログ */}
      {pwResetConfirm && (
        <div className="dialog-overlay">
          <div className="dialog">
            <div className="dialog-header">
              <h2>🔑 パスワードリセットの確認</h2>
            </div>
            <div className="dialog-body">
              <p style={{ marginBottom: 8 }}>
                <strong>{pwResetTargetUser?.display_name || pwResetTargetUser?.username}</strong> のパスワードをリセットします。
              </p>
              <p style={{ fontSize: '13px', color: '#6B7280', background: '#FEF9C3', border: '1px solid #FDE047', borderRadius: 8, padding: '10px 12px' }}>
                新しい仮パスワードが発行され、ユーザーの登録メールアドレスにも送信されます。ユーザーは次回ログイン時にパスワードの変更が必要です。
              </p>
            </div>
            <div className="dialog-footer">
              <button className="dialog-cancel" onClick={() => setPwResetConfirm(false)} disabled={pwResetLoading}>
                キャンセル
              </button>
              <button
                className="dialog-save"
                style={{ background: '#D97706' }}
                onClick={handlePasswordReset}
                disabled={pwResetLoading}
              >
                {pwResetLoading ? (<><span className="btn-inline-spinner" />リセット中...</>) : 'リセットする'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* パスワードリセット結果ダイアログ */}
      {pwResetResult && (
        <div className="dialog-overlay">
          <div className="dialog">
            <div className="dialog-header">
              <h2>{pwResetResult === 'error' ? '❌ エラー' : '✅ リセット完了'}</h2>
            </div>
            <div className="dialog-body">
              {pwResetResult === 'error' ? (
                <p style={{ color: '#DC2626' }}>パスワードリセットに失敗しました。もう一度お試しください。</p>
              ) : (
                <>
                  {pwResetResult.emailSent ? (
                    <p>
                      <strong>{pwResetTargetUser?.display_name || pwResetTargetUser?.username}</strong> の仮パスワードを発行し、登録メールアドレスに送付しました。
                    </p>
                  ) : (
                    <>
                      <p style={{ marginBottom: 12 }}>
                        <strong>{pwResetTargetUser?.display_name || pwResetTargetUser?.username}</strong> の仮パスワードを発行しましたが、メールの送付に失敗しました。
                        下記の仮パスワードをユーザーに直接伝えてください。
                      </p>
                      <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: '12px 16px', textAlign: 'center' }}>
                        <p style={{ fontSize: '0.8rem', color: '#B45309', marginBottom: 6 }}>仮パスワード</p>
                        <p style={{ fontSize: '1.3rem', fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.1em', color: '#7C2D12', margin: 0 }}>
                          {pwResetResult.password}
                        </p>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="dialog-footer">
              <button className="dialog-save" onClick={() => { setPwResetResult(null); setPwResetTargetUser(null); }}>
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MFAリセット確認ダイアログ */}
      {mfaResetConfirm && (
        <div className="dialog-overlay">
          <div className="dialog">
            <div className="dialog-header">
              <h2>🔐 MFAリセットの確認</h2>
            </div>
            <div className="dialog-body">
              <p style={{ marginBottom: 8 }}>
                <strong>{selectedUser?.display_name || selectedUser?.username}</strong> の二段階認証をリセットします。
              </p>
              <p style={{ fontSize: '13px', color: '#6B7280', background: '#FEF9C3', border: '1px solid #FDE047', borderRadius: 8, padding: '10px 12px' }}>
                {selectedUser?.role === 'admin'
                  ? 'このユーザーは管理者（パスキー）です。リセット後は次回ログイン時にパスキーの再設定が必要になります。既に登録済みのパスキー自体はCognito側の仕様上取り消せません。'
                  : 'リセット後、そのユーザーは次回ログイン時にMFAの再設定が必要になります。'}
              </p>
            </div>
            <div className="dialog-footer">
              <button
                className="dialog-cancel"
                onClick={() => setMfaResetConfirm(false)}
                disabled={mfaResetLoading}
              >
                キャンセル
              </button>
              <button
                className="dialog-save"
                style={{ background: '#EF4444' }}
                onClick={handleMfaReset}
                disabled={mfaResetLoading}
              >
                {mfaResetLoading ? (<><span className="btn-inline-spinner" />リセット中...</>) : 'リセットする'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Settings;